import { Config, Listings, SagisConfig } from 'containers/AppContainer/typings';
import { PROCEDURE_TYPES } from 'containers/LawyerLegalProceedingsPage/constants';
import { translate } from 'containers/services/translate';
import {
  compact,
  first,
  get,
  isEmpty,
  isEqual,
  isNil,
  isNull,
  map,
  uniqueId,
} from 'lodash';
import {
  CALCULATION_START_DATE,
  CASABLANCA,
  CLIENT,
  COURT_NUMBER,
  DOCUMENT_NATURE,
  IDD,
  JUDGEMENT_DATE,
  JUDICIARY_PAYMENT_NATURE,
  LINK_BENEFICIARY_RECIPIENT,
  PAYMENT_CHANNEL,
  RECIPIENT,
  REGULATION_LEVEL_REQUIREMENT,
  REGULATION_STATUS,
  RISKS,
  TYPES,
  NONE,
  PAYMENT_METHODS,
  MOROCCO_COUNTRY,
  RABAT_CITY,
  RISK,
  CDRA_BAR,
  NEXT_OF_KIN,
  ABANDON,
  VICTIM_TYPES,
  STOP_ENTRY,
  JUDGEMENT_ID_JOM,
  REGULATION_PAYMENT_STATUS_TO_PAY,
  REGULATION_PAYMENT_STATUS_ON_HOLD,
  SHEETS,
  CDRA_BOARD,
  PAID_STATUS,
  BNEJ_CITIES,
  FEES,
  TO_BE_PAID_STATUS,
  CHECK_PRINT_MAX_USERNAME_LENGTH,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import {
  Beneficiary,
  Vehicule,
  Victim,
} from 'containers/PreOpeningPage/typings';
import {
  PAYMENT_STATUS,
  PROVISION,
  REGULATIONS,
  REGULATION_AMOUNTS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { RISK_NAME } from 'containers/CourtManagerLegalProceedings/Provision/constants';
import moment from 'moment-timezone';
import { INJURY_POST_DECEASED } from 'containers/PreOpeningPage/constants';
import { getVictimAge } from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import {
  CorpoRegulation,
  RcCorporeal,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import {
  getCities,
  getCountriesWithCities,
} from 'containers/RecapManagerPage/utils';
import { NORMAL_DATE_FORMAT } from 'constants/global';
import {
  DEDUCTION_NO_RATE_IDD,
  IDD_CHECK,
  RELIQUAT_APPEL,
  RELIQUAT_JUGEMENT_TITLE,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';
import {
  STATUS_PENDING,
  STATUS_REJECTED,
} from 'containers/LawyerFeeNote/constants';
import { JudgementOnMeritsVictim } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  enableRemainingAmountField,
  enableRemainingJudgement,
  isPartialExecution,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/utils';
import {
  getExecutionByVictim,
  getLatestRegulations,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RiskAmount/utils';
import { JUDGEMENT_ON_MERITS_VICTIM } from 'containers/data-structure';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { validationCourtFileNumber } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';
import {
  appConfigListingTyping,
  BeneficiaryType,
  VictimType,
} from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';

export const getTypes = (messages: any, props: any) => {
  return [
    {
      title: 'victims',
      message: translate(messages.riskVictims, props),
      expand: true,
    },
    {
      title: 'vehicles',
      message: translate(messages.riskVehicles, props),
      expand: true,
    },
    {
      title: 'additionalGuarantees',
      message: translate(messages.riskGarenties, props),
      expand: true,
    },
  ];
};
export const getDocumentNature = (
  documentNature: SagisConfig[] = [],
  judgementOnMerits: any,
  rcCorpo: any,
  sagisClaim: any,
  isRecap: boolean,
  paymentStatus: any,
  isBasicManagementRegulation?: boolean,
) => {
  const substituteDocumentNature: SagisConfig[] = [];

  const { hasBoth, hasRegulation } = areVictimsRegulated(
    rcCorpo,
    sagisClaim,
    paymentStatus,
  );

  const isAppeal = isBasicManagementRegulation
    ? get(rcCorpo, 'regulationInfo.isAppeal', false)
    : get(judgementOnMerits, 'procedureType', {}) === PROCEDURE_TYPES.APPEAL;
  documentNature.forEach((documentNature: SagisConfig) => {
    switch (documentNature.ItemDescription) {
      case DOCUMENT_NATURE.REMAINDER.title: {
        return (
          (hasRegulation || hasBoth || isRecap) &&
          substituteDocumentNature.push({
            ...documentNature,
            ItemDescription: DOCUMENT_NATURE.REMAINDER.substitute,
          })
        );
      }
      case DOCUMENT_NATURE.PARTIAL.title: {
        return (
          ((!isAppeal && !hasRegulation) || hasBoth || isRecap) &&
          substituteDocumentNature.push({
            ...documentNature,
            ItemDescription: DOCUMENT_NATURE.PARTIAL.substitute,
          })
        );
      }
      case DOCUMENT_NATURE.TOTAL_JUDGEMENT.title: {
        return !isAppeal && (!hasRegulation || hasBoth || isRecap)
          ? substituteDocumentNature.push({
              ...documentNature,
              ItemDescription: DOCUMENT_NATURE.TOTAL_JUDGEMENT.substitute,
            })
          : null;
      }
      case DOCUMENT_NATURE.TOTAL_APPEAL.title: {
        return isAppeal && (!hasRegulation || hasBoth || isRecap)
          ? substituteDocumentNature.push({
              ...documentNature,
              ItemDescription: DOCUMENT_NATURE.TOTAL_APPEAL.substitute,
            })
          : null;
      }
    }
  });
  return substituteDocumentNature;
};
export const getCheckedBeneficiaries = (rcCorpo: any) => {
  const beneficiaries: number[] = [];
  get(rcCorpo, 'data', []).forEach((victim: Victim) =>
    get(victim, 'beneficiaries', []).forEach((beneficiary: Beneficiary) => {
      beneficiaries.push(beneficiary.id);
    }),
  );
  return beneficiaries;
};
export const regulationInfoValidator = (
  riskRecap: any,
  judgementOnMeritsByVictims: any,
  judgementOnMeritsByVehicles: any,
  setIsRegulationValidated: Function,
  isRegulationValidated: any,
  activeStep: number,
  vehicleAlert: string,
  victimAlert: string,
  isJomValidated: any,
  isBasicManagement?: boolean,
) => {
  let regulationStepValidator = { ...isRegulationValidated };
  const risks = [...riskRecap];
  const { rcCorpo, rcMaterial } = getRisks(riskRecap);
  const regulationInfoCorpo = get(
    risks[risks.indexOf(rcCorpo)],
    'regulationInfo',
    {},
  );
  const materialRegulationInfo = get(
    risks[risks.indexOf(rcMaterial)],
    'regulationInfo',
    {},
  );

  const processNatureCorpo = get(regulationInfoCorpo, [
    REGULATIONS.PROCESS_NATURE,
  ]);

  const processNatureMaterial = get(materialRegulationInfo, [
    REGULATIONS.PROCESS_NATURE,
  ]);
  /* istanbul ignore next */
  if (isBasicManagement) {
    regulationStepValidator = {
      ...isRegulationValidated,
      [activeStep]: {
        ...regulationStepValidator[activeStep],
        activeStep: !validationCourtFileNumber(
          get(regulationInfoCorpo, REGULATIONS.COURT_NUMBER),
        ),
      },
    };
  } else {
    const corpoCondition =
      !isEmpty(rcCorpo) &&
      isEmpty(judgementOnMeritsByVictims) &&
      !isNil(processNatureCorpo);

    const materialCondition =
      !isEmpty(rcMaterial) &&
      isEmpty(judgementOnMeritsByVehicles) &&
      !isNil(processNatureMaterial);

    materialCondition && !isNil(processNatureMaterial)
      ? (regulationStepValidator = {
          ...isRegulationValidated,
          [activeStep]: {
            activeStep: true,
            message: {
              ...get(regulationStepValidator[activeStep], 'message', ''),
              vehicles: vehicleAlert,
            },
          },
        })
      : (regulationStepValidator = {
          ...isRegulationValidated,
          [activeStep]: {
            activeStep: Object.values(
              get(regulationStepValidator[activeStep], 'message', ''),
            ).some(item => !isEmpty(item)),
            message: {
              ...get(regulationStepValidator[activeStep], 'message', ''),
              vehicles: '',
            },
          },
        });
    corpoCondition && !isNil(processNatureCorpo)
      ? (regulationStepValidator = {
          ...isRegulationValidated,
          [activeStep]: {
            activeStep: true,
            message: {
              ...get(regulationStepValidator[activeStep], 'message', ''),
              victims: victimAlert,
            },
          },
        })
      : (regulationStepValidator = {
          ...isRegulationValidated,
          [activeStep]: {
            activeStep: Object.values(
              get(regulationStepValidator[activeStep], 'message', ''),
            ).some(item => !isEmpty(item)),
            message: {
              ...get(regulationStepValidator[activeStep], 'message', ''),
              victims: '',
            },
          },
        });

    if (
      (!isEmpty(rcCorpo) && !corpoCondition && isNil(processNatureCorpo)) ||
      isJomValidated[RISK.CORPOREAL] ||
      (!isEmpty(rcMaterial) &&
        !materialCondition &&
        isNil(processNatureMaterial)) ||
      isJomValidated[RISK.MATERIAL]
    )
      regulationStepValidator = {
        ...isRegulationValidated,
        [activeStep]: {
          ...regulationStepValidator[activeStep],
          activeStep: true,
        },
      };
  }

  setIsRegulationValidated(regulationStepValidator);
};
export const getRisks = (riskRecap: any) => {
  const rcCorpo =
    riskRecap.find((risk: any) => risk.riskName === RISK_NAME.CORPOREAL) || {};
  const rcMaterial =
    riskRecap.find((risk: any) => risk.riskName === RISK_NAME.MATERIEL) || {};
  const additionalWarrenties =
    riskRecap.find(
      (risk: any) => risk.riskName === RISK_NAME.GARANTIES_ANNEXES,
    ) || {};
  return { rcCorpo, rcMaterial, additionalWarrenties };
};
export const getJudiciarySettlementChannel = (
  paymentChannel: SagisConfig[],
  paymentNature: SagisConfig[],
  risk: any,
  riskRecap: any,
  setRiskRecap: any,
) => {
  const risks = [...riskRecap];

  const regulationInfo = get(risks[risks.indexOf(risk)], 'regulationInfo', {});
  const judiciaryPayment =
    paymentNature.find(item => item.ItemDescription === 'Judiciaire') || {};
  regulationInfo[REGULATIONS.PAYMENT_NATURE] = get(
    judiciaryPayment,
    'ItemKey',
    '',
  );

  const judiciaryChannel = paymentChannel
    .filter(item => item.ItemGroup === get(judiciaryPayment, 'ItemKey', ''))
    .filter(
      item =>
        item.ItemDescription !== PAYMENT_CHANNEL.CNRA.FORCED &&
        item.ItemDescription !== PAYMENT_CHANNEL.CNRA.FRIENDLY,
    );
  setRiskRecap(risks);

  return judiciaryChannel;
};
export const setPaymentChannelByBeneficiaryRecipient = (
  judiciaryChannel: SagisConfig[],
  regulationInfo: any,
  value: string,
) => {
  switch (value) {
    case RECIPIENT.BNEJ:
      const el =
        judiciaryChannel.find(
          item => item.ItemDescription === PAYMENT_CHANNEL.BNEJ,
        ) || {};
      regulationInfo[REGULATIONS.PAYMENT_CHANNEL] = get(el, 'ItemKey', '');
      break;
    case RECIPIENT.CDRA:
      const item =
        judiciaryChannel.find(
          item => item.ItemDescription === PAYMENT_CHANNEL.CDRA,
        ) || {};
      regulationInfo[REGULATIONS.PAYMENT_CHANNEL] = get(item, 'ItemKey', '');
      break;
  }
};
export const adaptJudgementMeritsInfo = (
  regulationInfo: any,
  judgementOnMerits: any,
) => {
  regulationInfo[REGULATIONS.JUDGEMENT_DATE] = !isEmpty(
    get(judgementOnMerits, JUDGEMENT_DATE, null),
  )
    ? formatDate(get(judgementOnMerits, JUDGEMENT_DATE, null))
    : null;

  regulationInfo[REGULATIONS.JUDGEMENT_DATE_JAF] = null;
  regulationInfo[REGULATIONS.CALCULATION_START_DATE] = get(
    judgementOnMerits,
    CALCULATION_START_DATE,
    null,
  );
  regulationInfo[REGULATIONS.COURT_NUMBER] = get(
    judgementOnMerits,
    COURT_NUMBER,
    null,
  );
  regulationInfo[REGULATIONS.JUDGEMENT_ID] = get(
    judgementOnMerits,
    JUDGEMENT_ID_JOM,
    null,
  );
  regulationInfo[REGULATIONS.IS_IDD_CHECKED] = get(
    judgementOnMerits,
    IDD,
    null,
  );
  regulationInfo[REGULATIONS.IS_APPEAL] =
    get(judgementOnMerits, 'procedureType') === PROCEDURE_TYPES.APPEAL
      ? true
      : false;
  regulationInfo[
    REGULATIONS.VALIDATOR
  ] = currentUserStorage?.get()?.username?.toUpperCase();
  regulationInfo[REGULATIONS.STATUS] = get(judgementOnMerits, 'status', '');
  regulationInfo[REGULATIONS.RC] = getSharedResponsibility(judgementOnMerits);
  regulationInfo[REGULATIONS.WITH_CONTESTATION] = !isEmpty(
    get(judgementOnMerits, 'jom.jmc', {}),
  );
};
export const addDefaultJomData = (
  riskRecap: any,
  setRiskRecap: Function,
  judgementOnMeritsByVictims: any,
  judgementOnMeritsByVehicles: any,
) => {
  const { rcCorpo, rcMaterial } = getRisks(riskRecap);
  const risks = [...riskRecap];

  applyJomByRisk(risks, rcCorpo, judgementOnMeritsByVictims);
  applyJomByRisk(risks, rcMaterial, judgementOnMeritsByVehicles);

  setRiskRecap(risks);
};
const applyJomByRisk = (risks: any, risk: any, judgementOnMerits: any) => {
  const { withAppeal, withFirstInstance } = hasAllProcedureTypes(
    judgementOnMerits,
    [],
  );
  const regulationInfo = get(risks[risks.indexOf(risk)], 'regulationInfo', {});
  checkSimilarity(regulationInfo, judgementOnMerits);
  if (
    (!isNil(regulationInfo[REGULATIONS.PROCESS_NATURE]) &&
      isEmpty(regulationInfo[REGULATIONS.JUDGEMENT_DATE])) ||
    get(risk.regulationInfo, REGULATIONS.IS_APPEAL) !==
      (withAppeal && withFirstInstance)
  ) {
    const judgementOnMerit = first(judgementOnMerits) || {};
    adaptJudgementMeritsInfo(regulationInfo, judgementOnMerit);
  }
};
export const getJomValidatorMessage = (risk: any, riskValidation: any) => {
  const currentRisk =
    RISKS.find((el: any) => {
      return el.riskName === risk.riskName;
    }) || {};

  return !isNil(riskValidation) && !isEmpty(currentRisk)
    ? riskValidation[get(currentRisk, 'item', '')]
    : null;
};
export const getPaymentNature = (paymentNature: SagisConfig[]) => {
  return (
    paymentNature.find(
      (item: SagisConfig) => item.ItemDescription === JUDICIARY_PAYMENT_NATURE,
    ) || {}
  );
};
export const paymentMethod = (paymentMethod: SagisConfig[]) => {
  return paymentMethod.filter(
    (item: SagisConfig) =>
      item.ItemDescription === PAYMENT_METHODS.CHECK_LETTER ||
      item.ItemDescription === PAYMENT_METHODS.TRANSFER,
  );
};
export const transferPayment = (paymentMethod: SagisConfig[]) => {
  const transferPayment =
    paymentMethod.find(
      (item: SagisConfig) => item.ItemDescription === PAYMENT_METHODS.TRANSFER,
    ) || {};

  return get(transferPayment, 'ItemKey', '');
};
export const checkLetterPayment = (paymentMethod: SagisConfig[]) => {
  const checkLetterPayment =
    paymentMethod.find(
      (item: SagisConfig) =>
        item.ItemDescription === PAYMENT_METHODS.CHECK_LETTER,
    ) || {};

  return get(checkLetterPayment, 'ItemKey', '');
};

export const formatDate = (date: Date | null) => {
  const formatedDate = moment(date as Date).format(NORMAL_DATE_FORMAT);
  return formatedDate === 'Invalid date' ? (date as any) : formatedDate;
};
export const convertDateFormat = (
  stringDate: any = '',
  isBasicManagement?: boolean,
) => {
  if (isBasicManagement) return stringDate;

  const [day, month, year] = stringDate.split('/');

  return `${year}-${month}-${day}`;
};
export const getJudgementOnMeritsByTargetValue = (
  judgementOnMerits: any,
  value: string,
) => {
  return (
    judgementOnMerits.find((j: any) => {
      return (
        formatDate(get(j, 'jom.judgementDate')) === value ||
        get(j, 'jom.judgementNumber') === value
      );
    }) || {}
  );
};
export const getJudgementOnMeritsByRisk = (
  judgementOnMerits: any,
  judgementOnMeritsWithCivjvm: any,
  regulationInfo: any,
) => {
  const { withAppeal, withFirstInstance } = hasAllProcedureTypes(
    judgementOnMerits,
    judgementOnMeritsWithCivjvm,
  );

  const judgementOnMerit =
    regulationInfo[REGULATIONS.JUDGEMENT_DATE] === NONE ||
    (!withAppeal && withFirstInstance) ||
    (withAppeal && withFirstInstance)
      ? judgementOnMerits.find((j: any) => {
          return (
            formatDate(get(j, 'jom.judgementDate')) ===
              (!isEmpty(regulationInfo[REGULATIONS.JUDGEMENT_DATE_JAF])
                ? regulationInfo[REGULATIONS.JUDGEMENT_DATE_JAF]
                : regulationInfo[REGULATIONS.JUDGEMENT_DATE] === NONE
                ? regulationInfo[REGULATIONS.JUDGEMENT_DATE_JAF]
                : regulationInfo[REGULATIONS.JUDGEMENT_DATE]) &&
            get(j, 'jom.judgementNumber') ===
              (!isEmpty(regulationInfo[REGULATIONS.JUDGEMENT_COURT_NUMBER])
                ? regulationInfo[REGULATIONS.JUDGEMENT_COURT_NUMBER]
                : get(regulationInfo, 'courtNumber', ''))
          );
        }) || {}
      : {};
  const appealOnMerit =
    (withAppeal && !withFirstInstance) || (withAppeal && withFirstInstance)
      ? judgementOnMerits.find((j: any) => {
          return (
            formatDate(get(j, 'jom.judgementDate')) ===
              regulationInfo[REGULATIONS.JUDGEMENT_DATE] &&
            get(j, 'jom.judgementNumber') ===
              get(regulationInfo, 'courtNumber', '')
          );
        }) || {}
      : {};
  return { appealOnMerit, judgementOnMerit };
};
export const getJudgementNature = (documentNature: SagisConfig[] = []) => {
  return documentNature.filter(
    el =>
      el.ItemDescription === DOCUMENT_NATURE.TOTAL_JUDGEMENT.title ||
      el.ItemDescription === DOCUMENT_NATURE.TOTAL_APPEAL.title,
  );
};
export const getCurrentRegulationNature = (
  documentNature: SagisConfig[] = [],
  regulationInfo: any,
) => {
  const totalDocumentNature = getJudgementNature(documentNature);
  const foundTotalJudgement =
    totalDocumentNature.find(el => {
      return el.ItemKey === regulationInfo[REGULATIONS.REGULATION_NATURE];
    }) || {};

  return foundTotalJudgement;
};
export const getLinkWithRecepient = (linkWithRecipient: SagisConfig[]) => {
  return linkWithRecipient.filter(
    (item: SagisConfig) =>
      item.ItemDescription !== LINK_BENEFICIARY_RECIPIENT.BNEJ &&
      item.ItemDescription !== LINK_BENEFICIARY_RECIPIENT.CNRA &&
      item.ItemDescription !== LINK_BENEFICIARY_RECIPIENT.CDRA,
  );
};
export const getLinkWithRecepientForMaterialRisk = (
  linkWithRecipient: SagisConfig[],
) => {
  return linkWithRecipient.filter(
    (item: SagisConfig) =>
      item.ItemDescription === LINK_BENEFICIARY_RECIPIENT.INSURED ||
      item.ItemDescription === LINK_BENEFICIARY_RECIPIENT.AGENT,
  );
};
export const isTransferBNEJActivated = (
  appConfigList: appConfigListingTyping[],
) => {
  return Boolean(
    Number(
      get(
        appConfigList?.find(
          (item: appConfigListingTyping) =>
            item.task === 'ACTIVATE_VIREMENT_BNEJ',
        ),
        'value',
        0,
      ),
    ),
  );
};
export const beneficiaryRecipientCases = (
  value: string,
  regulationInfo: any,
  paymentMethods: SagisConfig[],
  appConfigList: appConfigListingTyping[],
  bnejCities: any,
) => {
  switch (value) {
    case RECIPIENT.BNEJ:
      regulationInfo[REGULATIONS.REAL_BENEFICIARY] = RECIPIENT.BNEJ;
      const bnejCity =
        bnejCities?.find(
          (item: SagisConfig) =>
            item.ItemKey === get(regulationInfo, REGULATIONS.CITY_CODE),
        ) || {};
      regulationInfo[REGULATIONS.PAYMENT_METHOD] =
        isTransferBNEJActivated(appConfigList) &&
        bnejCity &&
        BNEJ_CITIES[get(bnejCity, 'ItemDescription', '')] === RECIPIENT.BNEJ
          ? transferPayment(paymentMethods)
          : checkLetterPayment(paymentMethods);
      break;

    default:
      regulationInfo[REGULATIONS.REAL_BENEFICIARY] = null;
      regulationInfo[REGULATIONS.ADDITIONAL_ADDRESS] = null;
      regulationInfo[REGULATIONS.BNEJ_TAX_AMOUNT] = null;
      break;
  }
};
export const getChosenCity = (
  towns: SagisConfig[],
  risk: any,
  type: string,
) => {
  return (
    towns.find(e => e.ItemKey === get(risk.regulationInfo, `${type}`, '')) ||
    ({} as SagisConfig)
  );
};

export const getDeceasedCompensationAmount = (
  judgementOnMerits: any,
  victim: VictimType,
) => {
  let compensationAmount: any;

  const jmv = get(judgementOnMerits, 'jom.jmv', []).find(
    (item: JudgementOnMeritsVictim) => {
      return get(item, 'victimId') === victim.id;
    },
  );
  if (!isEmpty(jmv)) {
    compensationAmount = get(jmv, 'compensationAmount', '');
  } else
    get(judgementOnMerits, 'jom.jomBeneficiaries', []).forEach(
      (jomBeneficiary: any) => {
        const foundVictim =
          get(jomBeneficiary, 'beneficiary.victim.id') === victim.id;

        if (foundVictim && isNil(compensationAmount))
          return (compensationAmount = get(
            jomBeneficiary,
            'beneficiary.victim.judiciaryAmount',
            '',
          ));
      },
    );
  return compensationAmount;
};
export const getBeneficiaryJudgementAmount = (
  judgementOnMerits: any,
  beneficiary: any,
) => {
  let jomBeneficiary = {} as any;
  get(judgementOnMerits, 'jom.jomBeneficiaries', []).forEach((item: any) => {
    const { beneficiary: beneficiaryVictim, ...rest } = item;
    const foundJomBeneficiary = get(item, 'beneficiaryId') === beneficiary.id;

    if (foundJomBeneficiary) return (jomBeneficiary = Object.assign({}, rest));
  });

  return jomBeneficiary;
};
export const getInjuredResponsibilityShare = (judgementOnMerits: any) => {
  let responsibilityShare: any;

  get(judgementOnMerits, 'jom.civjs', []).forEach((civj: any) => {
    return (responsibilityShare = get(civj, 'responsibilityShare'));
  });
  return responsibilityShare;
};
export const getInjuredCompensationAmount = (
  judgementOnMerits: any,
  victim: VictimType,
) => {
  let compensationAmount: any;
  let provisionalExecutionRate: any;
  get(judgementOnMerits, 'jom.civjs', []).forEach((civj: any) => {
    get(civj, 'civjmede', []).forEach((civjmede: any) => {
      const foundVictim = civjmede.victimId === victim.id;
      if (foundVictim) {
        compensationAmount = get(civjmede, 'compensationAmount', null);
        provisionalExecutionRate = get(civjmede, 'provisionalExecution', '');
        return { compensationAmount, provisionalExecutionRate };
      }
    });

    isNil(compensationAmount) &&
      get(civj, 'civjacce', []).forEach((civjacce: any) => {
        const foundVictim = civjacce.victimId === victim.id;

        if (foundVictim)
          compensationAmount = get(civjacce, 'calculatedRevenue', null);
        provisionalExecutionRate = get(civjacce, 'provisionalExecution', '');
        return { compensationAmount, provisionalExecutionRate };
      });
  });
  return { compensationAmount, provisionalExecutionRate };
};
export const getDeceasedVictimResponsibility = (
  judgementOnMerit: any,
  victim: VictimType,
) => {
  const jmv =
    get(judgementOnMerit, 'jom.jmv', []).find(
      (item: JudgementOnMeritsVictim) => {
        return get(item, 'victimId') === victim?.id;
      },
    ) || {};
  return get(jmv, 'responsibilityShare');
};
export const getDeceasedVictimInfoByJudiciaryType = (
  judgementOnMerit: any,
  value: string,
  victim: VictimType,
) => {
  const jmv =
    get(judgementOnMerit, 'jom.jmv', []).find(
      (item: JudgementOnMeritsVictim) => {
        return get(item, 'victimId') === victim?.id;
      },
    ) || {};
  return get(jmv, value, '');
};
export const getCompensationAmountByJudiciaryType = (
  judgementOnMeritsByVictims: any,
  judgementOnMeritsWithCivjvm: any,
  rcCorporeal: any,
  victim: VictimType,
) => {
  const { judgementOnMerit, appealOnMerit } = getJudgementOnMeritsByRisk(
    judgementOnMeritsByVictims,
    judgementOnMeritsWithCivjvm,
    get(rcCorporeal, 'regulationInfo', {}),
  );
  const currentJom = !get(
    rcCorporeal.regulationInfo,
    REGULATIONS.IS_APPEAL,
    false,
  )
    ? judgementOnMerit
    : appealOnMerit;

  switch (victim.injuryPostKey) {
    case INJURY_POST_DECEASED:
      const judgementDeceasedCompensationAmount = getDeceasedCompensationAmount(
        judgementOnMerit,
        victim,
      );
      const appealDeceasedCompensationAmount = getDeceasedCompensationAmount(
        appealOnMerit,
        victim,
      );

      return {
        judgementCompensationAmount: judgementDeceasedCompensationAmount,
        appealCompensationAmount: appealDeceasedCompensationAmount,
        [JUDGEMENT_ON_MERITS_VICTIM.FUNERAL_FEES]: getDeceasedVictimInfoByJudiciaryType(
          currentJom,
          JUDGEMENT_ON_MERITS_VICTIM.FUNERAL_FEES,
          victim,
        ),
        [JUDGEMENT_ON_MERITS_VICTIM.MEDICAL_FEES]: getDeceasedVictimInfoByJudiciaryType(
          currentJom,
          JUDGEMENT_ON_MERITS_VICTIM.MEDICAL_FEES,
          victim,
        ),
        responsibilityShare: getDeceasedVictimResponsibility(
          currentJom,
          victim,
        ),
        provisionalExecutionRate: '',
      };
    default:
      const {
        compensationAmount: judgementCompensationAmount,
      } = getInjuredCompensationAmount(judgementOnMerit, victim);

      const {
        compensationAmount: appealCompensationAmount,
      } = getInjuredCompensationAmount(appealOnMerit, victim);
      return {
        judgementCompensationAmount,
        appealCompensationAmount,
        responsibilityShare: getInjuredResponsibilityShare(currentJom),
        provisionalExecutionRate:
          get(
            getInjuredCompensationAmount(currentJom, victim),
            'provisionalExecutionRate',
            '',
          ) || '',
        [JUDGEMENT_ON_MERITS_VICTIM.MEDICAL_FEES]: null,
        [JUDGEMENT_ON_MERITS_VICTIM.FUNERAL_FEES]: null,
      };
  }
};
export const getBeneficiaryAmountByJudiciaryType = (
  judgementOnMeritsByVictims: any,
  judgementOnMeritsWithCivjvm: any,
  rcCorporeal: any,
  beneficiary: any,
) => {
  const { judgementOnMerit, appealOnMerit } = getJudgementOnMeritsByRisk(
    judgementOnMeritsByVictims,
    judgementOnMeritsWithCivjvm,
    get(rcCorporeal, 'regulationInfo', {}),
  );
  const jomBeneficiary = getBeneficiaryJudgementAmount(
    judgementOnMerit,
    beneficiary,
  );
  const appealBeneficiary = getBeneficiaryJudgementAmount(
    appealOnMerit,
    beneficiary,
  );
  return { jomBeneficiary, appealBeneficiary };
};
export const getDamageAmount = (
  judgementOnMeritsByVehicles: any,
  rcMaterial: any,
  vehicle: Vehicule,
) => {
  const { judgementOnMerit, appealOnMerit } = getJudgementOnMeritsByRisk(
    judgementOnMeritsByVehicles,
    [],
    get(rcMaterial, 'regulationInfo', {}),
  );
  const judgementDamageAmount = getDamageAmountByJudiciaryType(
    judgementOnMerit,
    vehicle,
  );
  const appealDamageAmount = getDamageAmountByJudiciaryType(
    appealOnMerit,
    vehicle,
  );
  return { judgementDamageAmount, appealDamageAmount };
};
export const getDamageAmountByJudiciaryType = (
  judgementOnMerits: any,
  vehicle: Vehicule,
) => {
  let damageAmount: any;
  get(judgementOnMerits, 'jom.civjs', []).forEach((civj: any) => {
    get(civj, 'civjmece', []).forEach((civjmece: any) => {
      const foundVehicle = civjmece.vehiculeId === vehicle.id;
      if (foundVehicle)
        return (damageAmount = get(civjmece, 'damageAmount', null));
    });
    isNil(damageAmount) &&
      get(civj, 'civjacce', []).forEach((civjacce: any) => {
        const foundVehicle = civjacce.vehiculeId === vehicle.id;
        if (foundVehicle)
          return (damageAmount = get(civjacce, 'evaluationAmount', null));
      });
  });
  return damageAmount;
};
export const getRegulationNature = (
  riskRecap: any,
  setRiskRecap: any,
  documentNature: SagisConfig[],
  sagisClaim: any,
  paymentStatus: SagisConfig[],
) => {
  const { rcCorpo } = getRisks(riskRecap);
  const risks = [...riskRecap];
  const regulationInfo = get(
    risks[risks.indexOf(rcCorpo)],
    'regulationInfo',
    {},
  );

  const totalJudgementNature = getCurrentRegulationNature(
    documentNature,
    regulationInfo,
  );
  if (!isEmpty(get(rcCorpo, 'data', [])))
    rcCorpo.data = get(rcCorpo, 'data', []).map((victim: VictimType) => {
      return {
        ...victim,
        regulationAmount: {
          ...((get(victim, 'regulationAmount', {}) as unknown) as {}),
          executionRate: getExecutionRate(
            totalJudgementNature,
            regulationInfo,
            sagisClaim,
            victim,
            paymentStatus,
          ),
          isTotalExecution: !isEmpty(totalJudgementNature) ? true : false,
        },
      };
    });
  setRiskRecap(riskRecap);
};
export const getExecutionRate = (
  totalJudgementNature: any,
  regulationInfo: any,
  sagisClaim: any,
  victim: VictimType,
  paymentStatus: SagisConfig[],
) => {
  const hasRemainingAmount = enableRemainingAmountField(
    regulationInfo?.natureCode,
  );
  return !isEmpty(totalJudgementNature) &&
    get(regulationInfo, [REGULATIONS.IS_APPEAL], false)
    ? 0
    : !isEmpty(totalJudgementNature) &&
      !get(regulationInfo, [REGULATIONS.IS_APPEAL], false)
    ? 100
    : hasRemainingAmount
    ? get(
        getExecutionByVictim(
          get(sagisClaim, 'regulations'),
          victim,
          paymentStatus,
        ),
        'executionRate',
      )
    : '';
};
export const getUpdatedJudgementOnMerits = (
  riskRecap: any,
  requestGetJudgementOnMerits: any,
  claimRecap: ClaimRecapType,
  risk: any,
) => {
  const { rcCorpo, rcMaterial } = getRisks(riskRecap);
  const risks = [...riskRecap];
  const regulationInfoCorpo = get(
    risks[risks.indexOf(rcCorpo)],
    'regulationInfo',
    {},
  );
  const materialRegulationInfo = get(
    risks[risks.indexOf(rcMaterial)],
    'regulationInfo',
    {},
  );
  risk.riskName === RISK_NAME.CORPOREAL &&
    requestGetJudgementOnMerits(
      get(claimRecap, 'claim.id'),
      get(rcCorpo, 'data', []).map((rcCorpo: any) => rcCorpo.id),
      getCheckedBeneficiaries(rcCorpo),
      get(regulationInfoCorpo, REGULATIONS.PROCESS_NATURE, ''),
    );
  risk.riskName === RISK_NAME.MATERIEL &&
    requestGetJudgementOnMerits(
      get(claimRecap, 'claim.id'),
      get(risk, 'data', []).map((rc: any) => rc.id),
      get(materialRegulationInfo, REGULATIONS.PROCESS_NATURE, ''),
      null,
    );
};
export const checkSimilarity = (
  regulationInfo: any,
  judgementOnMerits: any,
) => {
  const jom =
    judgementOnMerits.find((jom: any) => {
      return (
        get(jom, 'jom.judgementNumber', '') ===
          get(regulationInfo, 'courtNumber') &&
        formatDate(get(jom, 'jom.judgementDate', '')) ===
          (get(regulationInfo, 'judgementDate', null) !== NONE
            ? get(regulationInfo, 'judgementDate', null)
            : get(regulationInfo, REGULATIONS.JUDGEMENT_DATE_JAF, null))
      );
    }) || {};

  isEmpty(jom) && adaptJudgementMeritsInfo(regulationInfo, judgementOnMerits);
};
export const getRegulationStatus = (regulationStatus: SagisConfig[]) =>
  regulationStatus.find(el => el.ItemDescription === REGULATION_STATUS) || {};
export const getLevelRequirement = (levelRequirements: SagisConfig[]) =>
  levelRequirements.find(
    el => el.ItemDescription === REGULATION_LEVEL_REQUIREMENT,
  ) || {};
export const fillDefaultFieldRcMaterial = (
  listings: Listings,
  riskRecap: any,
  setRiskRecap: Function,
  risk: any,
) => {
  const risks = [...riskRecap];
  const regulationInfo = get(risks[risks.indexOf(risk)], 'regulationInfo', {});
  fillDefaultFieldsByRecipient(listings, regulationInfo, risk);
  setRiskRecap(risks);
};
export const fillDefaultCorpoFields = (
  riskRecap: any,
  setRiskRecap: any,
  listings: Listings,
) => {
  const regulationStatus = getRegulationStatus(
    get(listings, 'EncryptDocumentCorpoStatus', []),
  );

  const levelRequirements = getLevelRequirement(
    get(listings, 'EncryptDocumentLevelRequirement', []),
  );
  const { rcCorpo } = getRisks(riskRecap);
  const risks = [...riskRecap];
  const regulationInfoCorpo = get(
    risks[risks.indexOf(rcCorpo)],
    'regulationInfo',
    {},
  );
  regulationInfoCorpo[REGULATIONS.PAYMENT_NATURE] = get(
    getPaymentNature(get(listings, 'PaymentNature', [])),
    'ItemKey',
    '',
  );
  regulationInfoCorpo[REGULATIONS.STATUS_CODE] = get(
    regulationStatus,
    'ItemKey',
    '',
  );

  regulationInfoCorpo[REGULATIONS.LEVEL_REQUIREMENT] = get(
    levelRequirements,
    'ItemKey',
    '',
  );
  regulationInfoCorpo[REGULATIONS.REFERENCE] = get(
    regulationInfoCorpo,
    [REGULATIONS.BENEFICIARY_REFERENCE],
    '',
  );
  regulationInfoCorpo[REGULATIONS.TPI] = !regulationInfoCorpo[
    REGULATIONS.IS_APPEAL
  ]
    ? get(regulationInfoCorpo, [REGULATIONS.COURT_NUMBER], '')
    : '';

  regulationInfoCorpo[REGULATIONS.APPEAL_COURTS] = regulationInfoCorpo[
    REGULATIONS.IS_APPEAL
  ]
    ? get(regulationInfoCorpo, [REGULATIONS.COURT_NUMBER], '')
    : '';

  fillDefaultFieldsByRecipient(listings, regulationInfoCorpo, rcCorpo);
  setRiskRecap(risks);
};
export const fillDefaultFieldsByRecipient = (
  listings: Listings,
  regulationInfo: any,
  risk: any,
) => {
  switch (get(regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '')) {
    case RECIPIENT.BNEJ: {
      const linkToBeneficiary =
        get(listings, 'LinesOfRecipt', []).find(
          (lineOfReceipt: SagisConfig[]) =>
            get(lineOfReceipt, 'ItemDescription', '') ===
            LINK_BENEFICIARY_RECIPIENT.BNEJ,
        ) || {};
      regulationInfo[REGULATIONS.ADDITIONAL_ADDRESS] =
        regulationInfo[REGULATIONS.REAL_BENEFICIARY];
      if (risk.riskName === RISK_NAME.CORPOREAL) {
        regulationInfo[REGULATIONS.LINK_TO_BENEFICIARY] = get(
          linkToBeneficiary,
          'ItemKey',
          '',
        );
      }
      return (regulationInfo[REGULATIONS.ADDITIONAL_ADDRESS] =
        regulationInfo[REGULATIONS.REAL_BENEFICIARY]);
    }
    case RECIPIENT.CDRA: {
      const linkToBeneficiary =
        get(listings, 'LinesOfRecipt', []).find(
          (lineOfReceipt: SagisConfig[]) =>
            get(lineOfReceipt, 'ItemDescription', '') ===
            LINK_BENEFICIARY_RECIPIENT.CDRA,
        ) || {};
      if (risk.riskName === RISK_NAME.CORPOREAL) {
        regulationInfo[REGULATIONS.LINK_TO_BENEFICIARY] = get(
          linkToBeneficiary,
          'ItemKey',
          '',
        );
      }
      regulationInfo[REGULATIONS.CITY_CODE] = get(
        getCityCodeByDefault(listings),
        'ItemKey',
        '',
      );
      return (regulationInfo[REGULATIONS.ADDITIONAL_ADDRESS] =
        regulationInfo[REGULATIONS.REAL_BENEFICIARY]);
    }
    case RECIPIENT.OTHERS: {
      const linkToBeneficiary =
        get(listings, 'LinesOfRecipt', []).find(
          (lineOfReceipt: SagisConfig[]) =>
            get(lineOfReceipt, 'ItemDescription', '') ===
            regulationInfo[REGULATIONS.REAL_BENEFICIARY],
        ) || {};

      if (risk.riskName === RISK_NAME.CORPOREAL) {
        regulationInfo[REGULATIONS.LINK_TO_BENEFICIARY] = get(
          linkToBeneficiary,
          'ItemKey',
          '',
        );
      }
      return (regulationInfo[REGULATIONS.CITY_CODE] = get(
        getCityCodeByDefault(listings),
        'ItemKey',
        '',
      ));
    }
  }
};
export const getCityCodeByDefault = (listings: Listings) =>
  get(listings, 'EncryptDocumentCity', []).find(
    (item: SagisConfig) => item.ItemKey === CASABLANCA,
  ) || {};
export const getCityCodeByRisk = (risk: any, listings: Listings) => {
  const encryptDocumentCity =
    get(listings, 'EncryptDocumentCity', []).find(
      (item: SagisConfig) =>
        item.ItemKey === get(risk.regulationInfo, [REGULATIONS.CITY_CODE]),
    ) || {};
  return get(encryptDocumentCity, 'ItemDescription', '');
};

export const getVictimDetails = (
  rcCorpo: RcCorporeal,
  sagisClaim: any,
  corporealEncryptedDocuments: any = [],
  paymentStatus: SagisConfig[],
) => {
  return get(rcCorpo, 'data', [])
    .map((victim: VictimType) => {
      const showRemainder = enableRemainingJudgement(
        get(rcCorpo, 'regulationInfo.natureCode'),
      );
      const minor =
        isMinor(
          victim,
          { victim },
          rcCorpo,
          get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_DATE, ''),
        ) && isMinorVictimCNRA(corporealEncryptedDocuments, { victim });

      if (!isEmpty(get(victim, 'beneficiaries', []))) {
        const adultBeneficiaryExists = get(victim, 'beneficiaries', []).some(
          (nextOfKin: any) => {
            return !isMinor(
              victim,
              { nextOfKin },
              rcCorpo,
              get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_DATE, []),
            );
          },
        );

        return get(victim, 'beneficiaries', []).map((nextOfKin: any) => {
          const isMinorBeneficiary = isMinor(
            victim,
            { nextOfKin },
            rcCorpo,
            get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_DATE, []),
          );
          return {
            victimKey: parseInt(get(victim, 'victimKey', '')),
            id: get(victim, 'id'),
            victimName: `${get(victim, 'lastName')} ${get(
              victim,
              'firstName',
            )}`,
            nextOfKinKey: get(nextOfKin, 'nextOfKinKey', null),
            nextOfKinName: `${get(nextOfKin, 'lastName', '')} ${get(
              nextOfKin,
              'firstName',
              '',
            )}`,
            judgementAmount: !!get(nextOfKin, 'jomBeneficiary.judgementAmount')
              ? parseFloat(
                  removeCommaSeperator(
                    String(get(nextOfKin, 'jomBeneficiary.judgementAmount')),
                  ),
                )
              : undefined,
            arrestAmount: !!get(nextOfKin, 'appealBeneficiary.judgementAmount')
              ? parseFloat(
                  removeCommaSeperator(
                    String(get(nextOfKin, 'appealBeneficiary.judgementAmount')),
                  ),
                )
              : undefined,
            minor: isMinorBeneficiary,
            injuryPostKey: get(victim, 'injuryPostKey'),
            executionRate: get(victim, 'regulationAmount.executionRate'),
            manualExecutionProvAmount: showRemainder
              ? removeCommaSeperator(
                  String(
                    get(
                      nextOfKin,
                      'manualExecutionProvAmount',
                      get(
                        getExecutionByVictim(
                          get(sagisClaim, 'regulations', []),
                          victim,
                          paymentStatus,
                          nextOfKin,
                        ),
                        'executionProvAmount',
                      ),
                    ),
                  ),
                )
              : undefined,
            manualMedicalExecutionProvAmount: showRemainder
              ? get(
                  getExecutionByVictim(
                    get(sagisClaim, 'regulations', []),
                    victim,
                    paymentStatus,
                    nextOfKin,
                    FEES.MEDICAL_FEES,
                  ),
                  'executionProvAmount',
                )
              : undefined,
            manualFuneralExecutionProvAmount: showRemainder
              ? get(
                  getExecutionByVictim(
                    get(sagisClaim, 'regulations', []),
                    victim,
                    paymentStatus,
                    nextOfKin,
                    FEES.FUNERAL_FEES,
                  ),
                  'executionProvAmount',
                )
              : undefined,
            medicalFees:
              adultBeneficiaryExists && isMinorBeneficiary
                ? 0
                : !!get(
                    victim.regulationAmount,
                    REGULATION_AMOUNTS.MEDICAL_FEES,
                  )
                ? parseFloat(
                    removeCommaSeperator(
                      String(
                        get(
                          victim.regulationAmount,
                          REGULATION_AMOUNTS.MEDICAL_FEES,
                        ),
                      ),
                    ),
                  )
                : !isNil(
                    get(
                      victim.regulationAmount,
                      REGULATION_AMOUNTS.MEDICAL_FEES,
                      null,
                    ),
                  )
                ? parseFloat(
                    get(
                      victim.regulationAmount,
                      REGULATION_AMOUNTS.MEDICAL_FEES,
                      null,
                    ),
                  )
                : null,
            [REGULATION_AMOUNTS.MEDICAL_FEES_REASON]: get(
              victim.regulationAmount,
              REGULATION_AMOUNTS.MEDICAL_FEES_REASON,
              null,
            ),
            funeralFees:
              adultBeneficiaryExists && isMinorBeneficiary
                ? 0
                : parseFloat(
                    get(
                      victim.regulationAmount,
                      REGULATION_AMOUNTS.FUNERAL_FEES,
                    ),
                  )
                ? parseFloat(
                    removeCommaSeperator(
                      String(
                        get(
                          victim.regulationAmount,
                          REGULATION_AMOUNTS.FUNERAL_FEES,
                        ),
                      ),
                    ),
                  )
                : !isNil(
                    get(
                      victim.regulationAmount,
                      REGULATION_AMOUNTS.FUNERAL_FEES,
                      null,
                    ),
                  )
                ? parseFloat(
                    get(
                      victim.regulationAmount,
                      REGULATION_AMOUNTS.FUNERAL_FEES,
                      null,
                    ),
                  )
                : null,
            [REGULATION_AMOUNTS.FUNERAL_FEES_REASON]: get(
              victim.regulationAmount,
              REGULATION_AMOUNTS.FUNERAL_FEES_REASON,
            ),
          };
        });
      }
      const arrestAmount = !!get(
        victim.regulationAmount,
        REGULATION_AMOUNTS.APPEAL_AMOUNT,
      )
        ? removeCommaSeperator(
            String(
              get(victim.regulationAmount, REGULATION_AMOUNTS.APPEAL_AMOUNT),
            ),
          )
        : undefined;
      const judgementAmount = get(
        victim.regulationAmount,
        REGULATION_AMOUNTS.COMPENSATION_AMOUNT,
      );

      const companyAmount = get(
        victim.regulationAmount,
        REGULATION_AMOUNTS.COMPANY_AMOUNT,
      );

      return {
        id: get(victim, 'id'),
        injuryPostKey: get(victim, 'injuryPostKey'),
        victimKey: parseInt(get(victim, 'victimKey', '')),
        victimName: `${get(victim, 'lastName')} ${get(victim, 'firstName')}`,
        judgementAmount,
        arrestAmount,
        cnraAmount: !isEmpty(companyAmount)
          ? parseFloat(removeCommaSeperator(companyAmount))
          : undefined,
        minor,
        executionRate: get(victim, 'regulationAmount.executionRate'),
        manualExecutionProvAmount: showRemainder
          ? removeCommaSeperator(
              String(
                get(
                  victim,
                  'regulationAmount.manualExecutionProvAmount',
                  get(
                    getExecutionByVictim(
                      get(sagisClaim, 'regulations', []),
                      victim,
                      paymentStatus,
                      {},
                    ),
                    'executionProvAmount',
                  ),
                ),
              ),
            )
          : undefined,
        judgementGlobalAmount: !isEmpty(companyAmount)
          ? parseFloat(removeCommaSeperator(companyAmount))
          : undefined,
        isMinorVictimCorpo: isMinor(
          victim,
          { victim },
          rcCorpo,
          get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_DATE, ''),
        ),
      };
    })
    .flat();
};
export const getDocumentHeader = (
  rcCorpo: any,
  sagisClaim: any,
  substituteNatureRemainder: any,
  corporealEncryptedDocuments: any = [],
  paymentStatus: SagisConfig[],
  isBasicManagementRegulation?: boolean,
) => {
  const riskKey = parseInt(getRiskKey(sagisClaim, rcCorpo));
  const claimKey = getClaimKey(sagisClaim, riskKey);
  const { lossDate } = sagisClaim;
  const { isRemainder, isAppealedRemainder } = substituteNatureRemainder;
  const natureCode =
    get(rcCorpo.regulationInfo, REGULATIONS.IS_APPEAL) && isRemainder
      ? isAppealedRemainder
      : get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_NATURE);
  const opposingLawyer = get(
    rcCorpo.regulationInfo,
    [REGULATIONS.OPPOSING_LAWYER],
    '',
  );
  return {
    documentHeader: {
      claimKey,
      lossDate,
      riskKey: riskKey,
      responsabilitySharedRate: isBasicManagementRegulation
        ? get(
            rcCorpo.regulationInfo,
            [REGULATIONS.SHARED_RESPONSABILITY_RATE_WIHTOUT_JUDGEMENT],
            null,
          )
        : undefined,
      victimsDetailModel: getVictimDetails(
        rcCorpo,
        sagisClaim,
        corporealEncryptedDocuments,
        paymentStatus,
      ).flat(),
      categoryCode: get(
        rcCorpo.regulationInfo,
        [REGULATIONS.CATEGORY_CODE],
        '',
      ),
      statutCode: get(rcCorpo.regulationInfo, [REGULATIONS.STATUS_CODE], ''),
      natureCode,
      civilPartyLawyer: opposingLawyer !== null ? opposingLawyer : '',
      judgementDate:
        get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null) !==
          NONE && !get(rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL], false)
          ? convertDateFormat(
              get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null),
              isBasicManagementRegulation,
            )
          : convertDateFormat(
              get(
                rcCorpo.regulationInfo,
                [REGULATIONS.JUDGEMENT_DATE_JAF],
                null,
              ),
              isBasicManagementRegulation,
            ),
      arrestDate: get(rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL], false)
        ? convertDateFormat(
            get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null),
            isBasicManagementRegulation,
          )
        : '',
      settlementDate: get(
        rcCorpo.regulationInfo,
        [REGULATIONS.REGULATION_DATE],
        null,
      ),
      requirementLevelCode: get(
        rcCorpo.regulationInfo,
        [REGULATIONS.LEVEL_REQUIREMENT],
        null,
      ),
      reference: get(
        rcCorpo.regulationInfo,
        [REGULATIONS.BENEFICIARY_REFERENCE],
        '',
      ),
      TPI: get(rcCorpo.regulationInfo, [REGULATIONS.TPI], ''),
      cityCode: get(rcCorpo.regulationInfo, [REGULATIONS.CITY_CODE], ''),
      appealCourts: get(
        rcCorpo.regulationInfo,
        [REGULATIONS.APPEAL_COURTS],
        '',
      ),
      missionNumber: '',
    },
  };
};
export const getVictimWithNextOfKinKey = (
  rcCorpo: RcCorporeal,
  victimDetails: any,
) => {
  const isNextOfKinRealBeneficiary =
    get(rcCorpo.regulationInfo, REGULATIONS.REAL_BENEFICIARY) === NEXT_OF_KIN;

  return isNextOfKinRealBeneficiary
    ? {
        nextOfKinKey: get(
          getRandomNextOfKinKey(victimDetails),
          'nextOfKinKey',
          '',
        ),
        victimKey: get(getRandomNextOfKinKey(victimDetails), 'victimKey', ''),
      }
    : {};
};
export const getRandomNextOfKinKey = (victimDetails: any) => {
  return victimDetails.find((item: any) => get(item, 'nextOfKinKey', '')) || {};
};
export const formatRegulationCorporeal = (
  listings: Listings,
  corporealEncryptedDocuments: any,
  rcCorpo: any,
  sagisClaim: any,
  substituteNatureRemainder: any,
  encryptedDocumentsOfMinors: any,
  encryptedDocumentsOfAdults: any,
  corpoRegulations: any,
  paymentStatus: SagisConfig[],
  isBasicManagementRegulation?: boolean,
) => {
  const encryptDocumentToCreate = formatDocumentRegulation(
    corporealEncryptedDocuments,
    rcCorpo,
    sagisClaim,
    substituteNatureRemainder,
    paymentStatus,
    isBasicManagementRegulation,
  );

  const linkOfBeneficiaryNextOfKin = getVictimWithNextOfKinKey(
    rcCorpo,
    get(encryptDocumentToCreate, 'documentHeader.victimsDetailModel').filter(
      (item: any) => !item.minor,
    ),
  );

  const encryptedDocumentForMinors = {
    ...encryptDocumentToCreate,
    [REGULATIONS.BNEJ_TAX_AMOUNT]: '',
    [REGULATIONS.BNEJ_NUMBER]: '',
    [REGULATIONS.DIRECT_INDEMNITY_COSTS]: 0,
    documentHeader: {
      ...get(encryptDocumentToCreate, 'documentHeader', {}),
      victimsDetailModel: get(
        encryptDocumentToCreate,
        'documentHeader.victimsDetailModel',
      ).filter((item: any) => item.minor),
    },
  };
  const encryptedDocumentForAdults = {
    ...encryptDocumentToCreate,
    nextOfKinKey: get(linkOfBeneficiaryNextOfKin, 'nextOfKinKey', ''),
    documentHeader: {
      ...get(encryptDocumentToCreate, 'documentHeader', {}),
      victimsDetailModel: get(
        encryptDocumentToCreate,
        'documentHeader.victimsDetailModel',
      ).filter((item: any) => !item.minor),
    },
  };
  const withAdultRegulation = !isEmpty(
    get(encryptedDocumentsOfAdults, 'encryptedDocumentsOfAdults', []),
  );

  const CNRABeneficiary = getBeneficiary(
    listings,
    rcCorpo,
    get(encryptedDocumentForMinors, 'documentHeader.victimsDetailModel[0]', {}),
    true,
    get(sagisClaim, 'code', ''),
    encryptedDocumentsOfMinors,
    get(encryptedDocumentsOfMinors, 'encryptedDocumentsOfMinors', []),
    get(sagisClaim, 'regulations', []),
    corpoRegulations,
    withAdultRegulation,
    isBasicManagementRegulation,
  );
  const adultBeneficiary = getBeneficiary(
    listings,
    rcCorpo,
    get(encryptedDocumentForAdults, 'documentHeader.victimsDetailModel[0]', {}),
    false,
    get(sagisClaim, 'code', ''),
    encryptedDocumentsOfAdults,
    get(encryptedDocumentsOfAdults, 'encryptedDocumentsOfAdults', []),
    get(sagisClaim, 'regulations', []),
    corpoRegulations,
    withAdultRegulation,
    linkOfBeneficiaryNextOfKin,
    isBasicManagementRegulation,
  );
  return [
    {
      ...(!isEmpty(
        get(
          encryptedDocumentForMinors,
          'documentHeader.victimsDetailModel',
          [],
        ),
      )
        ? {
            encryptDocumentToCreate: encryptedDocumentForMinors,
            beneficiary: CNRABeneficiary,
            judgementId: get(rcCorpo, 'regulationInfo.judgementId'),
            opposingLawyer: getOpposingLawyer(true, rcCorpo, listings),
          }
        : {}),
    },
    {
      ...(!isEmpty(
        get(
          encryptedDocumentForAdults,
          'documentHeader.victimsDetailModel',
          [],
        ),
      )
        ? {
            encryptDocumentToCreate: encryptedDocumentForAdults,
            beneficiary: adultBeneficiary,
            judgementId: get(rcCorpo, 'regulationInfo.judgementId'),
            opposingLawyer: getOpposingLawyer(false, rcCorpo, listings),
          }
        : {}),
    },
  ].filter(item => !isEmpty(item));
};
export const getOpposingLawyer = (
  isMinor: boolean,
  rcCorpo: RcCorporeal,
  listings: Listings,
) => {
  if (
    !isEmpty(get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER)) ||
    !isEmpty(get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER_CITY)) ||
    !isEmpty(get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER_ADDRESS))
  ) {
    return {
      name: get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER),
      city: Number(
        get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER_CITY),
      ),
      address: get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER_ADDRESS),
      email: get(rcCorpo.regulationInfo, REGULATIONS.OPPOSING_LAWYER_EMAIL),
    };
  }
  return {};
};
export const formatProvision = (
  corporealEncryptedDocuments: any,
  sagisClaim: any,
  riskKey: string,
) => {
  const provisionInfo = getRiskCorpoProvision(sagisClaim, riskKey);
  const documentChiffreCorporel = get(
    corporealEncryptedDocuments[0],
    'documentChiffreCorporel',
    {},
  );
  return {
    interestsAmount: get(documentChiffreCorporel, 'CNRA.interestsAmount', ''),
    costsAmount: get(
      documentChiffreCorporel,
      'directCompensation.costsAmount',
      '',
    ),
    feesAmount: get(
      documentChiffreCorporel,
      'directCompensation.feesAmount',
      '',
    ),
    directCompensationInterestsAmount: get(
      documentChiffreCorporel,
      'directCompensation.interestsAmount',
      '',
    ),
    provisionAmount: parseFloat(get(provisionInfo, 'riskProvisionVictims')),
    date: moment().toISOString(true),
    riskID: get(provisionInfo, 'riskId'),
  };
};
export const getBeneficiary = (
  listings: Listings,
  rcCorpo: any,
  victim: VictimType,
  isMinor: boolean = false,
  claimNumber: string,
  encryptedDocuments: any,
  encryptedDocumentType: any = [],
  regulations: any = [],
  corpoRegulations: any,
  withAdultRegulation: boolean,
  linkOfBeneficiaryNextOfKin?: any,
  isBasicManagementRegulation?: boolean,
) => {
  const { country, city } = getCNRADestination(listings);
  const { id: cnraItemKey } = getPaymentChannelForCnra(
    get(rcCorpo, 'regulationInfo', {}),
    get(listings, 'PaymentChannel', []),
  );
  const victimKey = get(linkOfBeneficiaryNextOfKin, 'victimKey', '');
  const realBeneficiary = !isEmpty(
    get(rcCorpo.regulationInfo, REGULATIONS.REAL_BENEFICIARY, ''),
  )
    ? get(rcCorpo.regulationInfo, REGULATIONS.REAL_BENEFICIARY, '')
        .replace(CDRA_BOARD, '')
        .trim()
    : '';
  const cityCdra = getCDRACity(listings, realBeneficiary);

  return {
    key:
      get(rcCorpo.regulationInfo, REGULATIONS.REAL_BENEFICIARY) ===
        NEXT_OF_KIN && linkOfBeneficiaryNextOfKin
        ? `${victimKey}|VICTIM`
        : `${victim.victimKey}|VICTIM`,
    [REGULATIONS.COUNTRY_CODE]: country,
    claimNumber,
    [REGULATIONS.TOWN_CODE]: isMinor
      ? city
      : get(rcCorpo.regulationInfo, REGULATIONS.BENEFICIARY_RECIPIENT, '') ===
        RECIPIENT.CDRA
      ? cityCdra
      : get(rcCorpo.regulationInfo, [REGULATIONS.TOWN_CODE], ''),

    [REGULATIONS.BENEFICIARY_REFERENCE]: get(
      rcCorpo.regulationInfo,
      [REGULATIONS.BENEFICIARY_REFERENCE],
      '',
    ),
    [REGULATIONS.PAYMENT_NATURE]: get(
      rcCorpo.regulationInfo,
      [REGULATIONS.PAYMENT_NATURE],
      '',
    ),
    [REGULATIONS.PAYMENT_METHOD]:
      isMinor &&
      get(rcCorpo.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '') ===
        RECIPIENT.BNEJ &&
      isTransferBNEJActivated(get(listings, 'appConfig', []))
        ? checkLetterPayment(get(listings, 'PaymentMethod', []))
        : get(rcCorpo.regulationInfo, [REGULATIONS.PAYMENT_METHOD], ''),
    [REGULATIONS.PAYMENT_METHOD_CORPO]:
      isMinor &&
      get(rcCorpo.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '') ===
        RECIPIENT.BNEJ &&
      isTransferBNEJActivated(get(listings, 'appConfig', []))
        ? checkLetterPayment(get(listings, 'PaymentMethod', []))
        : get(rcCorpo.regulationInfo, [REGULATIONS.PAYMENT_METHOD], ''),
    [REGULATIONS.ADDITIONAL_ADDRESS]: isMinor
      ? RECIPIENT.CNRA
      : get(rcCorpo.regulationInfo, [REGULATIONS.ADDITIONAL_ADDRESS], ''),
    [REGULATIONS.BENEFICIARY_RECIPIENT]: isMinor
      ? RECIPIENT.CNRA
      : getBeneficiaryRecipient(
          rcCorpo,
          get(listings, 'EncryptDocumentCity', []),
        ),
    [REGULATIONS.REAL_BENEFICIARY]: isMinor
      ? RECIPIENT.CNRA
      : getRealBeneficiary(rcCorpo, get(listings, 'EncryptDocumentCity', [])),
    [REGULATIONS.PAYMENT_CHANNEL]: isMinor
      ? cnraItemKey
      : get(rcCorpo.regulationInfo, [REGULATIONS.PAYMENT_CHANNEL], ''),
    [REGULATIONS.LINK_TO_BENEFICIARY]: isMinor
      ? get(
          get(listings, 'LinesOfRecipt', []).find(
            (lineOfReceipt: SagisConfig[]) =>
              get(lineOfReceipt, 'ItemDescription', '') ===
              LINK_BENEFICIARY_RECIPIENT.CNRA,
          ) || {},
          'ItemKey',
          '',
        )
      : get(rcCorpo.regulationInfo, [REGULATIONS.LINK_TO_BENEFICIARY], ''),
    JudgementDate:
      get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], '') !== NONE
        ? convertDateFormat(
            get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], ''),
            isBasicManagementRegulation,
          )
        : convertDateFormat(
            get(rcCorpo.regulationInfo, [REGULATIONS.JUDGEMENT_DATE_JAF], ''),
            isBasicManagementRegulation,
          ),
    [REGULATION_AMOUNTS.AMOUNT]:
      get(encryptedDocuments, 'newAmount') !== 0 ||
      !isEmpty(get(encryptedDocuments, 'newAmount'))
        ? removeCommaSeperator(get(encryptedDocuments, 'newAmount'))
        : undefined,
    [REGULATIONS.PAYMENT_STATUS]: featureService.hasFeature(
      FEATURES.REGULATION_WORKFLOW_VALIDATION,
    )
      ? get(
          get(listings, 'PaymentStatus', []).find(
            (paymentStatus: SagisConfig) =>
              paymentStatus.ItemDescription ===
              REGULATION_PAYMENT_STATUS_ON_HOLD,
          ) || {},
          'ItemKey',
          '',
        )
      : get(
          get(listings, 'PaymentStatus', []).find(
            (paymentStatus: SagisConfig) =>
              paymentStatus.ItemDescription ===
              REGULATION_PAYMENT_STATUS_TO_PAY,
          ) || {},
          'ItemKey',
          '',
        ),
  };
};
export const formatDocumentRegulation = (
  corporealEncryptedDocuments: any,
  rcCorpo: any,
  sagisClaim: any,
  substituteNatureRemainder: any,
  paymentStatus: SagisConfig[],
  isBasicManagementRegulation?: boolean,
) => {
  const documentHeader = getDocumentHeader(
    rcCorpo,
    sagisClaim,
    substituteNatureRemainder,
    corporealEncryptedDocuments,
    paymentStatus,
    isBasicManagementRegulation,
  );
  const directIndemnityCosts = removeCommaSeperator(
    get(rcCorpo.regulationInfo, [REGULATIONS.DIRECT_INDEMNITY_COSTS]),
  );
  return {
    ...documentHeader,
    BNEJTaxAmount: removeCommaSeperator(
      get(rcCorpo.regulationInfo, [REGULATIONS.BNEJ_TAX_AMOUNT], 0),
    ),
    BNEJNumber:
      get(rcCorpo.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], null) ===
      RECIPIENT.BNEJ
        ? get(rcCorpo.regulationInfo, [REGULATIONS.BNEJ_NUMBER], '')
        : '',
    bankFees: 0,
    otherFees: 0,
    deductionRate:
      get(rcCorpo.regulationInfo, REGULATIONS.IS_IDD_CHECKED) ===
      IDD_CHECK.FALSE
        ? DEDUCTION_NO_RATE_IDD
        : get(rcCorpo.regulationInfo, [REGULATIONS.DEDUCTION_RATE], 0),
    deductionType: get(rcCorpo.regulationInfo, [REGULATIONS.DEDUCTION_TYPE]),
    directIndemnityCosts: !isEmpty(directIndemnityCosts)
      ? parseFloat(directIndemnityCosts)
      : 0,
  };
};

export const getBeneficiaryRecipient = (
  risk: any,
  bnejCities: SagisConfig[],
  isRecap?: boolean,
) => {
  if (isRecap) {
    return get(risk.regulationInfo, 'recipient', '');
  }
  switch (get(risk.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '')) {
    case RECIPIENT.OTHERS:
      return get(risk.regulationInfo, [REGULATIONS.OTHER_RECIPIENT], '');
    case RECIPIENT.CDRA:
      return `${get(
        risk.regulationInfo,
        [REGULATIONS.BENEFICIARY_RECIPIENT],
        '',
      )} ${CDRA_BAR} ${get(
        risk.regulationInfo,
        [REGULATIONS.REAL_BENEFICIARY],
        '',
      )}`;
    case RECIPIENT.BNEJ:
      const bnejCity =
        bnejCities.find(
          (item: SagisConfig) =>
            item.ItemKey === get(risk.regulationInfo, REGULATIONS.CITY_CODE),
        ) || {};
      return BNEJ_CITIES[get(bnejCity, 'ItemDescription', '')];
    default:
      return get(risk.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '');
  }
};
export const getRealBeneficiary = (
  risk: any,
  bnejCities: SagisConfig[],
  isRecap?: boolean,
) => {
  if (isRecap) {
    return get(risk.regulationInfo, 'realBeneficiary', '');
  }
  switch (get(risk.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '')) {
    case RECIPIENT.OTHERS:
      return get(risk.regulationInfo, [REGULATIONS.REAL_BENEFICIARY], '') ===
        RECIPIENT.OTHERS
        ? get(risk.regulationInfo, [REGULATIONS.OTHER_REAL_BENEFICIARY], '')
        : get(risk.regulationInfo, [REGULATIONS.REAL_BENEFICIARY], '');
    case RECIPIENT.CDRA:
      return `${get(
        risk.regulationInfo,
        [REGULATIONS.BENEFICIARY_RECIPIENT],
        '',
      )} BARREAU ${get(
        risk.regulationInfo,
        [REGULATIONS.REAL_BENEFICIARY],
        '',
      )}`;
    case RECIPIENT.BNEJ:
      const bnejCity =
        bnejCities.find(
          (item: SagisConfig) =>
            item.ItemKey === get(risk.regulationInfo, REGULATIONS.CITY_CODE),
        ) || {};
      return BNEJ_CITIES[get(bnejCity, 'ItemDescription', '')];

    default:
      return get(risk.regulationInfo, [REGULATIONS.REAL_BENEFICIARY], '');
  }
};
export const isMinorVictimCNRA = (
  corporealEncryptedDocuments: any = [],
  type: any,
) => {
  switch (Object.keys(type)[0]) {
    case TYPES.VICTIM:
      return !isEmpty(
        get(
          corporealEncryptedDocuments,
          '[0].documentChiffreCorporel.documentHeader.victimsDetailModel',
          [],
        ).find((item: any) => {
          return (
            !isEqual(get(item, 'indemnityCNRA'), 0) &&
            Number(type[TYPES.VICTIM].victimKey) === get(item, 'victimKey')
          );
        }) || {},
      );

    // case TYPES.BENEFICIARY:
    //   return !isEmpty(
    //     get(
    //       corporealEncryptedDocuments,
    //       '[0]documentChiffreCorporel.documentHeader.victimsDetailModel',
    //       [],
    //     ).find((item: any) => {
    //       return (
    //         !isEqual(get(item, 'indemnityCNRA'), 0) &&
    //         Number(type[TYPES.BENEFICIARY].nextOfKinKey) ===
    //           get(item, 'nextOfKinKey')
    //       );
    //     }) || {},
    //   );
  }
};

export const getClaimKey = (sagisClaim: any, riskId: any) => {
  return get(
    get(sagisClaim, 'offeredRisks', []).find(
      (el: any) => String(el.riskId) === String(riskId),
    ),
    'claimCode',
  );
};
export const getRiskKey = (sagisClaim: any, risk: any) => {
  let riskId = '';
  get(sagisClaim, 'offeredRisks', []).forEach((item: any) => {
    const foundRisk = risk.riskName === item.riskName;
    if (!foundRisk) {
      const additionalGarenty = item.riskName === risk.id;

      if (additionalGarenty) return (riskId = get(item, 'riskId', ''));
    } else return (riskId = get(item, 'riskId', ''));
  });
  return riskId;
};

export const getPaymentChannelForCnra = (
  regulationInfo: any,
  paymentChannel: SagisConfig[],
) => {
  const bnejPaymentChannel =
    paymentChannel.find(
      item => item.ItemDescription === PAYMENT_CHANNEL.CNRA.FORCED,
    ) || {};
  const cdraPaymentChannel =
    paymentChannel.find(
      item => item.ItemDescription === PAYMENT_CHANNEL.CNRA.FRIENDLY,
    ) || {};
  switch (get(regulationInfo, 'beneficiaryRecipient', '')) {
    case RECIPIENT.BNEJ:
      return {
        id: get(bnejPaymentChannel, 'ItemKey', ''),
        value: get(bnejPaymentChannel, 'ItemDescription', ''),
      };
    case RECIPIENT.CDRA:
      return {
        id: get(cdraPaymentChannel, 'ItemKey', ''),
        value: get(cdraPaymentChannel, 'ItemDescription', ''),
      };
    default:
      const defaultPaymentChannel =
        paymentChannel.find(
          item =>
            item.ItemKey ===
            get(regulationInfo, REGULATIONS.PAYMENT_CHANNEL, ''),
        ) || {};
      if (
        get(defaultPaymentChannel, 'ItemDescription', '') ===
        PAYMENT_CHANNEL.BNEJ
      )
        return {
          id: get(bnejPaymentChannel, 'ItemKey', ''),
          value: get(bnejPaymentChannel, 'ItemDescription', ''),
        };
      if (
        get(defaultPaymentChannel, 'ItemDescription', '') ===
        PAYMENT_CHANNEL.CDRA
      )
        return {
          id: get(cdraPaymentChannel, 'ItemKey', ''),
          value: get(cdraPaymentChannel, 'ItemDescription', ''),
        };
      else
        return {
          id: get(defaultPaymentChannel, 'ItemKey', ''),
          value: get(defaultPaymentChannel, 'ItemDescription', ''),
        };
  }
};

export const getVictims = (
  corporealEncryptedDocuments: any = [],
  rcCorpo: any,
) => {
  const victims: VictimType[] = [];
  corporealEncryptedDocuments.forEach((encryptedDocument: any) => {
    get(rcCorpo, 'data', []).forEach((victim: VictimType) => {
      const hasVictim =
        get(encryptedDocument, 'victimKey', '') === parseInt(victim.victimKey);

      const checkVictimExistance =
        victims.find((item: VictimType) => victim.id === item.id) || {};

      if (isEmpty(checkVictimExistance) && hasVictim) {
        victims.push(({
          ...victim,
          beneficiaries: [],
        } as unknown) as VictimType);
      }
      get(victim, 'beneficiaries', []).forEach(
        (beneficiary: BeneficiaryType) => {
          const hasBeneficiary =
            get(encryptedDocument, 'nextOfKinKey', '') ===
            parseInt((beneficiary.nextOfKinKey as unknown) as string);

          const beneficiaries = get(
            victims[victims.findIndex(item => item.id === victim.id)],
            'beneficiaries',
            [],
          ) as any;

          hasBeneficiary && beneficiaries.push(beneficiary);
        },
      );
    });
  });

  return victims;
};

export const getProcessNature = (listings: Listings, rcCorpo: RcCorporeal) => {
  const processNature =
    get(listings, 'procedureNature', []).find(
      process =>
        process.id ===
        get(rcCorpo.regulationInfo, [REGULATIONS.PROCESS_NATURE], ''),
    ) || {};

  return get(processNature, 'title', '');
};
export const getRegulationNatureTitle = (
  substituteDocumentNature: SagisConfig[],
  rcCorpo: RcCorporeal,
) => {
  const processNature =
    substituteDocumentNature?.find(
      substituteDocument =>
        substituteDocument.ItemKey ===
        get(rcCorpo.regulationInfo, [REGULATIONS.REGULATION_NATURE], ''),
    ) || {};

  return get(processNature, 'ItemDescription', '');
};
export const getCountryByCode = (listings: Listings) => {
  const countries: SagisConfig[] = getCountriesWithCities(
    get(listings, 'country', []),
    get(listings, 'villes', []),
  );
  const country =
    countries.find(
      (country: SagisConfig) => country.ItemDescription === MOROCCO_COUNTRY,
    ) || {};
  return get(country, 'ItemDescription', '');
};
export const getCityByCode = (rcCorpo: RcCorporeal, listings: Listings) => {
  const countries: SagisConfig[] = getCountriesWithCities(
    get(listings, 'country', []),
    get(listings, 'villes', []),
  );
  const country =
    countries.find(
      (country: SagisConfig) => country.ItemDescription === MOROCCO_COUNTRY,
    ) || {};
  const cities = getCities(
    get(listings, 'villes', []),
    get(country, 'ItemKey'),
  );

  const city =
    cities.find(
      (item: SagisConfig) =>
        item.ItemKey === get(rcCorpo.regulationInfo, [REGULATIONS.TOWN_CODE]),
    ) || {};
  return get(city, 'ItemDescription', '');
};
export const getOppositLawyerCityByCode = (
  rcCorpo: RcCorporeal,
  listings: Listings,
) => {
  const countries: SagisConfig[] = getCountriesWithCities(
    get(listings, 'country', []),
    get(listings, 'villes', []),
  );
  const country =
    countries.find(
      (country: SagisConfig) => country.ItemDescription === MOROCCO_COUNTRY,
    ) || {};
  const cities = getCities(
    get(listings, 'villes', []),
    get(country, 'ItemKey'),
  );
  const city =
    cities.find(
      (item: SagisConfig) =>
        parseInt(item.ItemKey) ===
        parseInt(
          get(rcCorpo.regulationInfo, [REGULATIONS.OPPOSING_LAWYER_CITY]),
        ),
    ) || {};
  return get(city, 'ItemDescription', '');
};
export const getPaymentChannel = (listings: Listings, risk: any) => {
  const paymentChannel =
    get(listings, 'PaymentChannel', []).find(
      (paymentChannel: SagisConfig[]) =>
        get(paymentChannel, 'ItemKey', '') ===
        get(risk.regulationInfo, [REGULATIONS.PAYMENT_CHANNEL], ''),
    ) || {};
  return get(paymentChannel, 'ItemDescription', '');
};

export const getSummarySheetAmounts = (
  victimKey: string = '',
  nextOfKinKey: any,
  corpoEncryptedDocumentsResult: any = [],
  fees?: String,
) => {
  const natureCode = get(
    corpoEncryptedDocumentsResult,
    '[0].documentChiffreCorporel.documentHeader.natureCode',
    '',
  );
  const isRemainderNature = enableRemainingAmountField(natureCode);

  const foundEncryptedDocument = getAmountByVictim(
    victimKey,
    nextOfKinKey,
    corpoEncryptedDocumentsResult,
    fees,
  );
  return {
    provisionalExecutionAmount:
      get(foundEncryptedDocument, 'indemnityCNRA', 0) !== 0 &&
      !isRemainderNature
        ? 0
        : numberWithCommas(
            String(
              get(foundEncryptedDocument, 'executionProvAmount', 0).toFixed(2),
            ),
          ),
    interestArrestAmount: resolveValue(
      get(foundEncryptedDocument, 'interestArrestAmount', 0),
    ),
    interestProvAmount: resolveValue(
      get(foundEncryptedDocument, 'interestProvAmount', 0),
    ),
    remainderAmount:
      get(foundEncryptedDocument, 'indemnityCNRA', 0) !== 0
        ? 0
        : resolveValue(get(foundEncryptedDocument, 'remainderAmount', 0)),
    indemnityCNRA: resolveValue(
      get(foundEncryptedDocument, 'indemnityCNRA', 0),
    ),
    arrestAmount: resolveValue(get(foundEncryptedDocument, 'arrestAmount', 0)),
    judgementAmount: resolveValue(
      get(foundEncryptedDocument, 'judgementAmount', 0),
    ),
  };
};
export const getFeesProvAmount = (
  victimKey: string = '',
  nextOfKinKey: any,
  corpoEncryptedDocumentsResult: any = [],
  fees?: String,
) => {
  const foundEncryptedDocument = getAmountByVictim(
    victimKey,
    nextOfKinKey,
    corpoEncryptedDocumentsResult,
    fees,
  );
  return {
    provisionalExecutionAmount: resolveValue(
      get(foundEncryptedDocument, 'executionProvAmount', 0),
    ),
    remainderAmount: resolveValue(
      get(foundEncryptedDocument, 'remainderAmount', 0),
    ),
  };
};
export const getRegulationRecapInfo = (
  victimKey: string = '',
  nextOfKinKey: any,
  corpoEncryptedDocumentsResult: any = [],
) => {
  const foundEncryptedDocument = getAmountByVictim(
    victimKey,
    nextOfKinKey,
    corpoEncryptedDocumentsResult,
  );

  return {
    provisionalExecutionAmount: numberWithCommas(
      String(get(foundEncryptedDocument, 'executionProvAmount', 0).toFixed(2)),
    ),
    interestArrestAmount: resolveValue(
      get(foundEncryptedDocument, 'interestArrestAmount', 0),
    ),
    interestProvAmount: resolveValue(
      get(foundEncryptedDocument, 'interestProvAmount', 0),
    ),
    remainderAmount: resolveValue(
      get(foundEncryptedDocument, 'remainderAmount', 0),
    ),
    indemnityCNRA: resolveValue(
      get(foundEncryptedDocument, 'indemnityCNRA', 0),
    ),
    arrestAmount: resolveValue(get(foundEncryptedDocument, 'arrestAmount', 0)),
    judgementAmount: resolveValue(
      get(foundEncryptedDocument, 'judgementAmount', 0),
    ),
  };
};
export const getAmountByVictim = (
  victimKey: string = '',
  nextOfKinKey: any,
  corpoEncryptedDocumentsResult: any = [],
  fees?: String,
) => {
  const foundEncryptedDocument = get(
    corpoEncryptedDocumentsResult,
    '[0].documentChiffreCorporel.documentHeader.victimsDetailModel',
    [],
  ).find((encryptedDocument: any) => {
    const hasFees = !!fees
      ? get(encryptedDocument, 'victimName', '') === fees
      : true;
    return (
      get(encryptedDocument, 'victimKey', '') === parseInt(victimKey) &&
      get(encryptedDocument, 'nextOfKinKey', '') ===
        (!isNull(nextOfKinKey) ? parseInt(nextOfKinKey) : nextOfKinKey) &&
      hasFees
    );
  });
  return foundEncryptedDocument;
};
export const resolveValue = (value: any) =>
  value === 0
    ? 0
    : typeof value === 'string'
    ? value
    : numberWithCommas(String(Number(value).toFixed(2)));
export const getAdditionalAddress = (rcCorporeal: any) => {
  if (
    get(rcCorporeal.regulationInfo, [REGULATIONS.BENEFICIARY_RECIPIENT], '') !==
    RECIPIENT.OTHERS
  ) {
    return get(rcCorporeal.regulationInfo, [REGULATIONS.REAL_BENEFICIARY]);
  }
  return get(rcCorporeal.regulationInfo, [REGULATIONS.ADDITIONAL_ADDRESS], '');
};
export const sumJudgementAmount = (
  victim: VictimType,
  compensationAmount: string,
  medicalFees: string,
  funeralFees: string,
  isAppeal: boolean,
) => {
  const jomBeneficiary = isAppeal ? 'appealBeneficiary' : 'jomBeneficiary';
  const totalJudgementAmount = ((get(
    victim,
    'beneficiaries',
    [],
  ) as unknown) as Array<BeneficiaryType>).reduce(
    (total: any, beneficiary: any) => {
      if (
        !isEmpty(get(beneficiary, `${jomBeneficiary}`, {})) &&
        !!get(beneficiary, `${jomBeneficiary}.judgementAmount`, {})
      ) {
        return (
          total +
          getAmountNumber(
            get(beneficiary, `${jomBeneficiary}.judgementAmount`, 0),
          )
        );
      }

      return total;
    },
    0,
  );

  const total =
    victim?.injuryPostKey !== INJURY_POST_DECEASED
      ? parseFloat(
          removeCommaSeperator(
            String(get(victim?.regulationAmount, `${compensationAmount}`, 0)),
          ),
        )
      : totalJudgementAmount +
        getAmountNumber(get(victim?.regulationAmount, `${medicalFees}`, 0)) +
        getAmountNumber(get(victim?.regulationAmount, `${funeralFees}`, 0));

  return total;
};
export const getTotalAmount = (rcCorpo: any) => {
  let appealAmount = 0;
  let judgementAmount = 0;
  get(rcCorpo, 'data', []).forEach((item: any) => {
    const judgement = sumJudgementAmount(
      item,
      REGULATION_AMOUNTS.COMPENSATION_AMOUNT,
      REGULATION_AMOUNTS.MEDICAL_FEES,
      REGULATION_AMOUNTS.FUNERAL_FEES,
      false,
    );

    judgementAmount = judgementAmount + judgement;
    const appeal = sumJudgementAmount(
      item,
      REGULATION_AMOUNTS.APPEAL_AMOUNT,
      REGULATION_AMOUNTS.MEDICAL_FEES,
      REGULATION_AMOUNTS.FUNERAL_FEES,
      true,
    );
    appealAmount = appealAmount + appeal;
  });

  return {
    appealAmount: resolveValue(appealAmount),
    judgementAmount: resolveValue(judgementAmount),
    totalAmount: get(rcCorpo.regulationInfo, 'isAppeal')
      ? resolveValue(appealAmount)
      : resolveValue(judgementAmount),
  };
};
export const getTotalEncryptedDocument = (
  rcCorpo: RcCorporeal,
  corpoEncryptedDocumentsResult: any = [],
) => {
  let totalRemainder = 0;
  let totalInterests = 0;
  let totalExecutionProvAmount = 0;
  let totalCNRA = 0;
  let totalJudgementInterests = 0;
  const victimDetails = get(
    corpoEncryptedDocumentsResult,
    '[0].documentChiffreCorporel.documentHeader.victimsDetailModel',
    [],
  );
  const natureCode = get(
    corpoEncryptedDocumentsResult,
    '[0].documentChiffreCorporel.documentHeader.natureCode',
    '',
  );
  const isRemainderNature = enableRemainingAmountField(natureCode);
  victimDetails.forEach((item: any) => {
    totalCNRA = totalCNRA + get(item, 'indemnityCNRA');
    totalInterests = get(rcCorpo.regulationInfo, REGULATIONS.IS_APPEAL, '')
      ? totalInterests + get(item, 'interestArrestAmount', 0)
      : totalInterests + get(item, 'interestProvAmount', 0);
    totalJudgementInterests =
      totalJudgementInterests + get(item, 'interestProvAmount', 0);
    totalRemainder =
      totalRemainder +
      getSagisAmountByVictim(item, get(item, 'remainderAmount', 0), false);
    totalExecutionProvAmount =
      totalExecutionProvAmount +
      getSagisAmountByVictim(
        item,
        get(item, 'executionProvAmount', 0),
        isRemainderNature,
      );
  });
  return {
    totalRemainder: resolveValue(totalRemainder),
    totalInterests: resolveValue(totalInterests),
    totalJudgementInterests: resolveValue(totalJudgementInterests),
    totalExecutionProvAmount: resolveValue(totalExecutionProvAmount),
    totalCNRA: resolveValue(totalCNRA),
  };
};

export const getAmountNumber = (value: any) => {
  return !!value ? parseFloat(removeCommaSeperator(String(value))) : 0;
};
export const getSagisAmountByVictim = (
  sagisResultByVictim: any,
  value: any,
  isRemainderNature: boolean,
) => {
  return get(sagisResultByVictim, 'indemnityCNRA') !== 0 && !isRemainderNature
    ? 0
    : value;
};
const getRiskNature = (risk: any, sagisClaim: any) => {
  let riskNature = '';
  const offeredRisk =
    get(sagisClaim, 'offeredRisks', []).find((el: any) => {
      return el.riskName === risk.riskName;
    }) || {};
  if (isEmpty(offeredRisk)) {
    get(risk, 'data', []).forEach((el: any) => {
      get(sagisClaim, 'offeredRisks', []).forEach((item: any) => {
        const foundRisk = String(el.id) === String(item.riskName);
        if (foundRisk) {
          return (riskNature = get(item, 'riskNature', ''));
        }
      });
    });
  } else return (riskNature = get(offeredRisk, 'riskNature', ''));
  return riskNature;
};
export const formatMaterialRisk = (
  risk: any,
  sagisClaim: any,
  listings: Listings,
  materialEncryptedDocuments: any,
) => {
  return get(risk, 'data', []).map((item: any) => {
    const JudgementDate = {
      ...(risk.riskName === RISK_NAME.MATERIEL
        ? {
            JudgementDate: get(
              risk.regulationInfo,
              [REGULATIONS.IS_APPEAL],
              false,
            )
              ? get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null)
              : !get(risk.regulationInfo, [REGULATIONS.IS_APPEAL], false) &&
                get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null) ===
                  NONE
              ? get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE_JAF], null)
              : get(risk.regulationInfo, [REGULATIONS.JUDGEMENT_DATE], null),
          }
        : undefined),
    };

    const regulation = {
      townCode: get(risk.regulationInfo, [REGULATIONS.TOWN_CODE], ''),
      recipient: getBeneficiaryRecipient(
        risk,
        get(listings, 'EncryptDocumentCity', []),
      ),
      documentTypeCode: '82', //décision judiciaire
      claimNumber: get(sagisClaim, 'code', ''),
      firstName:
        risk.riskName === RISK_NAME.MATERIEL
          ? get(item, 'firstName', '')
          : get(sagisClaim, 'clientFirstName', ''),
      lastName:
        risk.riskName === RISK_NAME.MATERIEL
          ? get(item, 'lastName', '')
          : get(sagisClaim, 'clientLastName', ''),
      riskName: getRiskName(risk, item),
      riskNature: getRiskNature(risk, sagisClaim),
      montantPrincipal: parseFloat(
        get(
          item.regulationAmount,
          [REGULATION_AMOUNTS.PRINCIPAL_AMOUNT],
          '',
        ).replace(',', ''),
      ),
      manualAmountToPay: isEmpty(get(item, REGULATION_AMOUNTS.AMOUNT, ''))
        ? parseFloat(
            getTotalAmountByRisk(
              risk,
              item,
              sagisClaim,
              materialEncryptedDocuments,
            ).replace(',', ''),
          )
        : parseFloat(get(item, REGULATION_AMOUNTS.AMOUNT, '').replace(',', '')),
      riskId:
        risk.riskName === RISK_NAME.GARANTIES_ANNEXES
          ? getRiskKey(sagisClaim, item)
          : getRiskKey(sagisClaim, risk),
      realBeneficiary: getRealBeneficiary(
        risk,
        get(listings, 'EncryptDocumentCity', []),
      ),
      beneficiaryReference: get(
        risk.regulationInfo,
        [REGULATIONS.BENEFICIARY_REFERENCE],
        '',
      ),
      rpRate: 1,
      adressRecipient: get(
        risk.regulationInfo,
        [REGULATIONS.ADDITIONAL_ADDRESS],
        '',
      ),
      beneficiaryCode:
        risk.riskName === RISK_NAME.MATERIEL
          ? `${getOpposingPartyKey(item, sagisClaim)}|PARTY`
          : getClientCode(sagisClaim),
      PaymentChannelCode: get(risk.regulationInfo, [
        REGULATIONS.PAYMENT_CHANNEL,
      ]),
      paymentModeCode: get(risk.regulationInfo, [REGULATIONS.PAYMENT_METHOD]),
      ...JudgementDate,
      isFromCorporel: true,
      paymentStatusCode: get(
        get(listings, 'PaymentStatus', []).find(
          (paymentStatus: SagisConfig) =>
            paymentStatus.ItemDescription === REGULATION_PAYMENT_STATUS_TO_PAY,
        ) || {},
        'ItemKey',
        '',
      ),
      isAgentRecipient:
        get(risk.regulationInfo, [REGULATIONS.LINK_TO_BENEFICIARY]) ===
        LINK_BENEFICIARY_RECIPIENT.INSURED
          ? false
          : true,
      PaymentNatureCode: get(risk.regulationInfo, [REGULATIONS.PAYMENT_NATURE]),
    };

    return regulation;
  });
};
export const getClientCode = (sagisClaim: any) => {
  const beneficiary = get(
    sagisClaim,
    'beneficiaries',
    [],
  ).find((beneficiary: any) => get(beneficiary, 'key', '').includes(CLIENT));
  return get(beneficiary, 'key', '');
};
export const getOpposingPartyKey = (item: any, sagisClaim: any) => {
  const corpoRisk =
    get(sagisClaim, 'offeredRisks', []).find(
      (item: any) => item.riskName === RISK_NAME.MATERIEL,
    ) || {};
  const opposingParty =
    get(corpoRisk, 'opposingParties', []).find((party: any) => {
      return (
        item.firstName === party.firstName &&
        item.lastName === party.lastName &&
        item.registrationNumber === party.registrationNumber
      );
    }) || {};

  return get(opposingParty, 'partyKey', '');
};
export const getRiskType = (sagisClaim: any, risk: any) => {
  const currentRisk =
    get(sagisClaim, 'offeredRisks', []).find(
      (item: any) => item.riskId === risk.riskId,
    ) || {};
  return get(currentRisk, 'riskName', '');
};
export const getBeneficiaryByRisk = (
  sagisClaim: any,
  beneficiaryCode: string,
) => {
  const beneficiary =
    (get(sagisClaim, 'beneficiaries', []) &&
      get(sagisClaim, 'beneficiaries', []).find(
        (item: any) => get(item, 'key', '') === beneficiaryCode,
      )) ||
    {};

  return !isEmpty(get(sagisClaim, 'beneficiaries', []))
    ? get(beneficiary, 'beneficiaryName', '').substring(
        0,
        get(beneficiary, 'beneficiaryName', '').indexOf('('),
      )
    : '';
};
export const getRiskName = (risk: any, item: any) => {
  if (get(risk, 'riskName', '') === RISK_NAME.GARANTIES_ANNEXES) {
    return get(item, 'id', '');
  }
  return get(risk, 'riskName', '');
};
export const getEncryptedTypeDocument = (
  sagisClaim: any,
  documentType: string,
) => {
  const corpoRisk =
    get(sagisClaim, 'offeredRisks', []).find(
      (item: any) => item.riskName === RISK_NAME.CORPOREAL,
    ) || {};
  const document =
    (get(corpoRisk, 'documentsType', []) &&
      get(corpoRisk, 'documentsType', []).find(
        (document: any) => document.key === documentType,
      )) ||
    {};
  return get(document, 'labelFR', '');
};
export const numberWithCommas = (item: any) => {
  let newValue = !isNil(item) ? item.replace(/[^0-9.-]/g, '') : '';
  return !isNil(newValue)
    ? newValue.replace(/^-?\d+/g, (match: any) =>
        match.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      )
    : null;
};
export const removeCommaSeperator = (item: string) => {
  return !isEmpty(item) ? item.replace(/,/g, '') : '';
};
export const isMinor = (
  victim: VictimType,
  type: any,
  rcCorpo: RcCorporeal,
  date: any,
) => {
  switch (Object.keys(type)[0]) {
    case TYPES.VICTIM:
      return (
        getVictimAge(type[TYPES.VICTIM]?.id, get(rcCorpo, 'data', []), date) <
        18
      );
    case TYPES.BENEFICIARY:
      return !isEmpty(get(type[TYPES.BENEFICIARY], 'dateOfBirth', null))
        ? getVictimAge(
            type[TYPES.BENEFICIARY]?.id,
            get(victim, 'beneficiaries', []),
            date,
          ) < 18
        : Number(get(type[TYPES.BENEFICIARY], 'age', 0)) < 18;
  }
};
export const getAppealDates = (judgementOnMerits: any) => {
  const judgements = judgementOnMerits.filter(
    (item: any) => get(item, 'procedureType', '') === PROCEDURE_TYPES.APPEAL,
  );
  const list = judgements.map((item: any) => ({
    id: item.jom.id,
    value: formatDate(item.jom.judgementDate),
  }));
  return [...list, { id: uniqueId(), value: NONE }];
};
export const hasAllProcedureTypes = (
  judgementOnMerits: any,
  judgementOnMeritsWithCivjvm: any,
) => {
  const withAppeal = judgementOnMerits.some(
    (item: any) => get(item, 'procedureType', '') === PROCEDURE_TYPES.APPEAL,
  );
  const withFirstInstance = [
    ...judgementOnMerits,
    ...(withAppeal && featureService.hasFeature(FEATURES.REGULATION_V2)
      ? judgementOnMeritsWithCivjvm
      : []),
  ].some(
    (item: any) =>
      get(item, 'procedureType', '') === PROCEDURE_TYPES.FIRST_INSTANCE,
  );
  return { withAppeal, withFirstInstance };
};
export const checkIsAppealProcess = (judgementOnMerit: any) => {
  return get(judgementOnMerit, 'procedureType') === PROCEDURE_TYPES.APPEAL;
};
export const setJudgementDate = (
  judgementOnMerits: any,
  judgementOnMerit: any,
  judgementOnMeritsWithCivjvm: any,
  name: string,
  value: any,
  judgementDate: any,
) => {
  const { withAppeal, withFirstInstance } = hasAllProcedureTypes(
    judgementOnMerits,
    judgementOnMeritsWithCivjvm,
  );
  switch (name) {
    case REGULATIONS.COURT_NUMBER:
      return withAppeal &&
        withFirstInstance &&
        checkIsAppealProcess(judgementOnMerit)
        ? formatDate(get(judgementOnMerit, JUDGEMENT_DATE, null))
        : !(withAppeal && withFirstInstance) &&
          !checkIsAppealProcess(judgementOnMerit)
        ? formatDate(get(judgementOnMerit, JUDGEMENT_DATE, null))
        : withAppeal &&
          !withFirstInstance &&
          checkIsAppealProcess(judgementOnMerit)
        ? formatDate(get(judgementOnMerit, JUDGEMENT_DATE, null))
        : NONE;

    case REGULATIONS.JUDGEMENT_DATE:
      return !isEmpty(get(judgementOnMerit, JUDGEMENT_DATE, null))
        ? formatDate(get(judgementOnMerit, JUDGEMENT_DATE, null))
        : value === NONE
        ? value
        : '';
    case REGULATIONS.JUDGEMENT_DATE_JAF:
      return !isEmpty(get(judgementOnMerit, JUDGEMENT_DATE, null)) &&
        judgementDate !== NONE
        ? formatDate(get(judgementOnMerit, JUDGEMENT_DATE, null))
        : value === NONE
        ? value
        : judgementDate === NONE
        ? judgementDate
        : '';
  }
};
export const getJudgementOnMeritsByJudgementDates = (
  judgementOnMerits: any,
  regulationInfo: any,
) => {
  const appealOnMerit =
    judgementOnMerits.find((j: any) => {
      return (
        formatDate(get(j, 'jom.judgementDate')) ===
          get(regulationInfo, 'judgementDate', '') &&
        get(j, 'jom.judgementNumber') === get(regulationInfo, 'courtNumber', '')
      );
    }) || {};

  const judgementOnMerit =
    judgementOnMerits.find((j: any) => {
      return (
        formatDate(get(j, 'jom.judgementDate')) ===
        regulationInfo[REGULATIONS.JUDGEMENT_DATE_JAF]
      );
    }) || {};
  return {
    appealOnMerit: isEmpty(judgementOnMerit) ? {} : appealOnMerit,
    judgementOnMerit: isEmpty(judgementOnMerit)
      ? appealOnMerit
      : judgementOnMerit,
  };
};
export const getPaymentMethod = (risk: any, listings: Listings) => {
  const paymentMethod =
    get(listings, 'PaymentMethod', []).find(
      (item: SagisConfig) =>
        item.ItemKey ===
        get(risk.regulationInfo, [REGULATIONS.PAYMENT_METHOD], ''),
    ) || {};
  return get(paymentMethod, 'ItemDescription', '');
};
export const getCDRACity = (listings: Listings, barCdra: string) => {
  const city =
    get(listings, 'bar', []).find((item: Config) => {
      return item.title === barCdra;
    }) || {};
  return get(city, 'sagisCode', '');
};
export const getCNRADestination = (listings: Listings) => {
  const countries: SagisConfig[] = getCountriesWithCities(
    get(listings, 'country', []),
    get(listings, 'villes', []),
  );
  const country =
    countries.find(
      (country: SagisConfig) => country.ItemDescription === MOROCCO_COUNTRY,
    ) || {};

  const cities = getCities(
    get(listings, 'villes', []),
    get(country, 'ItemKey', ''),
  );
  const city =
    cities.find((item: SagisConfig) => item.ItemDescription === RABAT_CITY) ||
    {};
  return {
    country: get(country, 'ItemKey', ''),
    city: get(city, 'ItemKey', ''),
    cityName: get(city, 'ItemDescription', ''),
  };
};
export const checkJudgementOnMeritsValidation = (
  riskRecap: any,
  risk: any,
  judgementOnMerits: any,
  isJomValidated: any,
  setIsJomValidated: Function,
  isBasicManagement?: boolean,
) => {
  const { rcMaterial, rcCorpo } = getRisks(riskRecap);
  isEmpty(rcMaterial) &&
    Object.assign(isJomValidated, { [RISK.MATERIAL]: false });
  isEmpty(rcCorpo) &&
    Object.assign(isJomValidated, { [RISK.CORPOREAL]: false });

  const selectedJom =
    judgementOnMerits.find((jom: any) => {
      const judgementDate = get(risk.regulationInfo, [
        REGULATIONS.JUDGEMENT_DATE,
      ]);
      const jomDate = formatDate(get(jom, JUDGEMENT_DATE));
      return judgementDate === NONE
        ? jomDate === get(risk.regulationInfo, REGULATIONS.JUDGEMENT_DATE_JAF)
        : jomDate === judgementDate;
    }) || {};

  const selectedJomStatus = get(selectedJom, 'status');

  const isJomValid =
    !isEmpty(selectedJomStatus) &&
    selectedJomStatus !== STATUS_PENDING &&
    selectedJomStatus !== STATUS_REJECTED;

  const propertyName =
    risk.riskName === RISK_NAME.CORPOREAL ? RISK.CORPOREAL : RISK.MATERIAL;
  return setIsJomValidated(
    isBasicManagement
      ? Object.assign(isJomValidated, { [propertyName]: false })
      : Object.assign(isJomValidated, { [propertyName]: !isJomValid }),
  );
};
export const isJudgementOnMeritsValidated = (
  rcCorpo: any,
  rcMaterial: any,
  isJomValidated: any,
) => {
  return (
    (get(isJomValidated, 'rcCorpo') &&
      !isEmpty(get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE))) ||
    (!isEmpty(get(rcMaterial.regulationInfo, REGULATIONS.JUDGEMENT_DATE)) &&
      get(isJomValidated, 'rcMaterial'))
  );
};
export const isPayableAmountUpdated = (
  rcCorpo: any,
  lossDate: string,
  encryptedDocuments: any,
) => {
  return get(rcCorpo, 'data', []).some((item: any) => {
    return encryptedDocuments.find((el: any) => {
      return (
        isEmpty(get(item, 'beneficiaries', [])) &&
        !isEmpty(get(item, REGULATION_AMOUNTS.AMOUNT, '')) &&
        get(item, 'victimKey') ===
          String(get(el, 'documentChiffreCorporel.victimDetail.victimKey')) &&
        parseFloat(
          get(item, REGULATION_AMOUNTS.AMOUNT, '').replace(',', ''),
        ) !== get(el, 'documentChiffreCorporel.totalAmount', '') &&
        getVictimAge(item.id, get(rcCorpo, 'data', []), lossDate) < 18
      );
    });
  });
};
export const checkMinorVictimAmount = (rcCorpo: any, lossDate: string) => {
  return get(rcCorpo, 'data', []).some((item: any) => {
    return (
      !isEmpty(get(item, REGULATION_AMOUNTS.AMOUNT)) &&
      getVictimAge(item.id, get(rcCorpo, 'data', []), lossDate) < 18
    );
  });
};
export const formatMaterialEncryptedDocument = (risk: any, sagisClaim: any) => {
  return get(risk, 'data', []).map((item: any) => {
    const partKey = getOpposingPartyKey(item, sagisClaim);

    return {
      riskId:
        risk.riskName === RISK_NAME.GARANTIES_ANNEXES
          ? getRiskKey(sagisClaim, item)
          : getRiskKey(sagisClaim, risk),
      partKey,
      principalAmount: !isEmpty(
        get(item, 'regulationAmount.principalAmount', ''),
      )
        ? parseFloat(
            get(item, 'regulationAmount.principalAmount').replace(',', ''),
          )
        : '',
      rpRate: 1,
      documentId: '82',
    };
  });
};
export const getTotalAmountByRisk = (
  risk: any,
  riskData: any,
  sagisClaim: any,
  materialEncryptedDocuments: any,
) => {
  const {
    material = [],
    additionalWarrenties = [],
  } = separateMaterialEncryptedDocument(materialEncryptedDocuments);

  switch (risk.riskName) {
    case RISK_NAME.MATERIEL:
      const materialDocument =
        material.find((item: any) => {
          const partKey = getOpposingPartyKey(riskData, sagisClaim);
          return item.partKey === partKey;
        }) || {};

      return !isEmpty(materialDocument)
        ? numberWithCommas(
            String(get(materialDocument, 'amountPayable', '').toFixed(3)),
          )
        : '';
    case RISK_NAME.GARANTIES_ANNEXES:
      const riskId = getRiskKey(sagisClaim, riskData);
      const additionalWarrentiesDocument =
        additionalWarrenties.find((el: any) => {
          return el.riskId === riskId;
        }) || {};

      return !isEmpty(additionalWarrentiesDocument)
        ? numberWithCommas(
            String(
              get(additionalWarrentiesDocument, 'amountPayable', '').toFixed(3),
            ),
          )
        : '';
  }
};
export const separateMaterialEncryptedDocument = (
  materialEncryptedDocuments: any,
) => {
  return {
    additionalWarrenties:
      materialEncryptedDocuments.filter((item: any) =>
        isEmpty(get(item, 'partKey')),
      ) || [],
    material:
      materialEncryptedDocuments.filter(
        (item: any) => !isEmpty(get(item, 'partKey')),
      ) || [],
  };
};
export const expandSideRegulation = (risk: any, riskRecap: any) => {
  const { rcCorpo, rcMaterial, additionalWarrenties } = getRisks(riskRecap);
  const expanded = {
    [risk.regulationId]:
      isEmpty(rcMaterial) &&
      isEmpty(additionalWarrenties) &&
      risk.riskName === RISK_NAME.CORPOREAL
        ? true
        : isEmpty(rcCorpo) &&
          isEmpty(additionalWarrenties) &&
          risk.riskName === RISK_NAME.MATERIEL
        ? true
        : isEmpty(rcCorpo) &&
          isEmpty(rcMaterial) &&
          risk.riskName === RISK_NAME.GARANTIES_ANNEXES
        ? true
        : false,
  };
  return expanded;
};

export const getIDDInfo = (rcCorpo: any) => {
  const deductionRate = get(
    rcCorpo.regulationInfo,
    REGULATIONS.DEDUCTION_RATE,
    '',
  );
  const part = 100 - deductionRate;
  const abandon =
    part === 100
      ? ABANDON.REFUSED
      : part === 0
      ? ABANDON.TOTAL
      : ABANDON.PARTIAL;
  return {
    part,
    abandon,
  };
};

export const getRegulationAmount = (
  rcCorpo: RcCorporeal,
  corpoEncryptedDocumentsResult: any,
) => {
  const interestsAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.directCompensation.interestsAmount',
        0,
      ).toFixed(2),
    ),
  );
  const compensationAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.directCompensation.compensationAmount',
        0,
      ).toFixed(2),
    ),
  );
  const totalAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.directCompensation.totalAmount',
        0,
      ).toFixed(2),
    ),
  );
  const directIndemnityCostAmount =
    get(rcCorpo.regulationInfo, REGULATIONS.BENEFICIARY_RECIPIENT) ===
    RECIPIENT.BNEJ
      ? get(rcCorpo.regulationInfo, REGULATIONS.DIRECT_INDEMNITY_COSTS, 0)
      : numberWithCommas(
          String(
            get(
              corpoEncryptedDocumentsResult,
              '[0].documentChiffreCorporel.directCompensation.costsAmount',
              0,
            ).toFixed(2),
          ),
        );

  const fees =
    get(rcCorpo.regulationInfo, REGULATIONS.BENEFICIARY_RECIPIENT) ===
    RECIPIENT.BNEJ
      ? get(
          corpoEncryptedDocumentsResult,
          '[0].documentChiffreCorporel.BNEJ.treasureAmount',
          0,
        ) +
        get(
          corpoEncryptedDocumentsResult,
          '[0].documentChiffreCorporel.BNEJ.transportAmount',
          0,
        ) +
        Number(get(rcCorpo.regulationInfo, REGULATIONS.BNEJ_TAX_AMOUNT, 0))
      : get(
          corpoEncryptedDocumentsResult,
          '[0].documentChiffreCorporel.directCompensation.feesAmount',
          0,
        );

  return {
    directIndemnityCostAmount,
    compensationAmount,
    fees: numberWithCommas(String(fees.toFixed(2))),
    totalAmount,
    interestsAmount,
  };
};
export const getCNRAAmounts = (corpoEncryptedDocumentsResult: any) => {
  const cnraCompensationAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.CNRA.compensationAmount',
        0,
      ).toFixed(2),
    ),
  );
  const cnraInterestsAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.CNRA.interestsAmount',
        0,
      ).toFixed(2),
    ),
  );

  const totalCnraAmount = numberWithCommas(
    String(
      get(
        corpoEncryptedDocumentsResult,
        '[0].documentChiffreCorporel.CNRA.totalAmount',
        0,
      ).toFixed(2),
    ),
  );

  return {
    cnraCompensationAmount,
    cnraInterestsAmount,
    totalCnraAmount,
  };
};
export const getRiskCorpoProvision = (sagisClaim: any, riskKey: string) => {
  const corpoRisk =
    get(sagisClaim, 'offeredRisks', []).find(
      (item: any) => item.riskName === RISK_NAME.CORPOREAL,
    ) || {};

  const oldProvisionDate = get(corpoRisk, 'provisionHistory', [])
    .map((e: any) => {
      return get(e, 'createdAt', '');
    })
    .sort()
    .reverse()[0];

  const oldProvision =
    get(corpoRisk, 'provisionHistory', []).find(
      (item: any) => item.createdAt === oldProvisionDate,
    ) || {};
  const intervenantProvision = get(sagisClaim, 'contractorFees', [])
    .filter((item: any) => String(item.riskKey) === String(riskKey))
    .reduce(
      (accumulator: any, currentValue: any) =>
        accumulator + get(currentValue, 'amount', 0),
      0,
    );
  return {
    riskProvision: numberWithCommas(
      String(get(corpoRisk, 'riskProvisions', '')),
    ),
    oldProvision: numberWithCommas(
      String(get(oldProvision, 'amount') + intervenantProvision),
    ),
    riskProvisionVictims: get(corpoRisk, 'riskProvisionVictims', ''),
    riskId: riskKey,
    responsibilityRate: get(corpoRisk, 'responsibilityRate', ''),
  };
};
export const getVictimType = (
  victim: VictimType,
  item: any,
  rcCorpo: RcCorporeal,
  sagisClaim: any,
) => {
  const itemType = {
    ...(isNil(get(item, 'nextOfKinKey'))
      ? { victim: { ...item } }
      : { nextOfKin: { ...item } }),
  };
  const minor = isMinor(
    victim,
    { ...itemType },
    rcCorpo,
    get(rcCorpo.regulationInfo, REGULATIONS.REGULATION_DATE, []),
  );
  return !isNil(get(item, 'nextOfKinKey')) && minor
    ? VICTIM_TYPES.MINOR_NEXT_OF_KIN
    : !isNil(get(item, 'nextOfKinKey')) && !minor
    ? VICTIM_TYPES.ADULT_NEXT_OF_KIN
    : isNil(get(item, 'nextOfKinKey')) && minor
    ? VICTIM_TYPES.MINOR_VICTIM
    : VICTIM_TYPES.ADULT_VICTIM;
};

export const getSumOfRegulations = (
  sagisClaim: any,
  paymentStatus: SagisConfig[],
) => {
  const paidStatus =
    paymentStatus.find(item => item.ItemDescription === PAID_STATUS) || {};
  const toBePaidStatus =
    paymentStatus.find(item => item.ItemDescription === TO_BE_PAID_STATUS) ||
    {};
  const regulations = !!get(sagisClaim, 'regulations', [])
    ? get(sagisClaim, 'regulations', []).filter(
        (item: any) =>
          item.paymentStatusCode === get(paidStatus, 'ItemKey') ||
          item.paymentStatusCode === get(toBePaidStatus, 'ItemKey'),
      )
    : [];
  const result = regulations.reduce((accumulator: any, currentItem: any) => {
    return accumulator + get(currentItem, 'amount', 0);
  }, 0);
  return resolveValue(result);
};
export const getSumOfContractorFees = (
  contractorFees: any,
  paymentStatus: SagisConfig[],
) => {
  const paidStatus =
    paymentStatus.find(item => item.ItemDescription === PAID_STATUS) || {};
  const toBePaidStatus =
    paymentStatus.find(item => item.ItemDescription === TO_BE_PAID_STATUS) ||
    {};
  const allAmounts = contractorFees.flatMap((item: any) =>
    get(item, 'regulations', [])
      .filter(
        (item: any) =>
          item.paymentStatusCode === get(paidStatus, 'ItemKey') ||
          item.paymentStatusCode === get(toBePaidStatus, 'ItemKey'),
      )
      .map((regulation: any) => regulation.amount),
  );
  const totalAmount = allAmounts.reduce(
    (acc: any, amount: any) => acc + amount,
    0,
  );
  return totalAmount;
};
export const isStopEntry = (
  rcCorpo: RcCorporeal,
  paymentMethod: SagisConfig[],
) => {
  const paymentModeCode = get(
    rcCorpo.regulationInfo,
    REGULATIONS.PAYMENT_METHOD,
  );
  const payment =
    paymentMethod.find(item => item.ItemDescription === STOP_ENTRY) || {};
  return get(payment, 'ItemKey', '') === paymentModeCode;
};
export const getVictimFieldLength = (rcCorpo: RcCorporeal) => {
  const victims = get(rcCorpo, 'data', []).length;
  let nextOfKins = 0;
  get(rcCorpo, 'data', []).forEach((item: any) => {
    nextOfKins = nextOfKins + get(item, 'beneficiaries', []).length * 2;
  });
  return victims + nextOfKins;
};
export const getCurrentRegulationAmount = (encryptedDocuments: any) => {
  return get(encryptedDocuments, 'newAmount') !== 0 &&
    !isEmpty(get(encryptedDocuments, 'newAmount'))
    ? numberWithCommas(get(encryptedDocuments, 'newAmount', '0'))
    : numberWithCommas(
        String(get(encryptedDocuments, 'totalAmount', 0).toFixed(2)),
      );
};
export const setJudgementAmount = (
  riskRecap: any,
  setRiskRecap: Function,
  judgementOnMeritsByVictims: any,
  judgementOnMeritsWithCivjvm: any,
  judgementOnMeritsByVehicles: any,
  regulations: any = [],
  paymentStatus: SagisConfig[],
  isBasicManagementRegulation?: boolean,
) => {
  const risks = [...riskRecap];
  const { rcCorpo, rcMaterial } = getRisks(risks);
  const isRemainder = enableRemainingAmountField(
    get(rcCorpo, 'regulationInfo.natureCode'),
  );
  get(risks[risks.indexOf(rcCorpo)], 'data', []).forEach(
    (victim: VictimType) => {
      const {
        judgementCompensationAmount,
        appealCompensationAmount,
        responsibilityShare,
        provisionalExecutionRate,
        funeralFees,
        medicalFees,
      } = getCompensationAmountByJudiciaryType(
        judgementOnMeritsByVictims,
        judgementOnMeritsWithCivjvm,
        rcCorpo,
        victim,
      );
      get(victim, 'beneficiaries', []).forEach((item: any) => {
        const {
          jomBeneficiary,
          appealBeneficiary,
        } = getBeneficiaryAmountByJudiciaryType(
          judgementOnMeritsByVictims,
          judgementOnMeritsWithCivjvm,
          rcCorpo,
          item,
        );
        item.manualExecutionProvAmount = get(
          getExecutionByVictim(regulations, victim, paymentStatus, item),
          'executionProvAmount',
        );

        item.jomBeneficiary =
          isRemainder &&
          !isNil(
            get(
              getExecutionByVictim(regulations, victim, paymentStatus, item),
              'judgementAmount',
            ),
          )
            ? {
                ...jomBeneficiary,
                judgementAmount: resolveValue(
                  get(
                    getExecutionByVictim(
                      regulations,
                      victim,
                      paymentStatus,
                      item,
                    ),
                    'judgementAmount',
                    '',
                  ),
                ),
              }
            : jomBeneficiary;
        item.appealBeneficiary = appealBeneficiary;
      });
      get(victim, 'regulationAmount', {})[
        REGULATION_AMOUNTS.COMPENSATION_AMOUNT
      ] =
        isRemainder &&
        !isNil(
          get(
            getExecutionByVictim(regulations, victim, paymentStatus),
            'judgementAmount',
          ),
        )
          ? resolveValue(
              get(
                getExecutionByVictim(regulations, victim, paymentStatus),
                'judgementAmount',
                '',
              ),
            )
          : judgementCompensationAmount;
      get(victim, 'regulationAmount', {})[
        REGULATION_AMOUNTS.APPEAL_AMOUNT
      ] = appealCompensationAmount;
      get(victim, 'regulationAmount', {})[
        REGULATION_AMOUNTS.FUNERAL_FEES
      ] = funeralFees;
      get(victim, 'regulationAmount', {})[
        REGULATION_AMOUNTS.MEDICAL_FEES
      ] = medicalFees;
      get(victim, 'regulationAmount', {})[
        REGULATION_AMOUNTS.RESPONSIBILITY_SHARE
      ] = isBasicManagementRegulation
        ? parseFloat(get(rcCorpo, 'regulationInfo.sharedResponsabilityRate'))
        : responsibilityShare;
      const isPartialNature = isPartialExecution(
        get(rcCorpo, 'regulationInfo.natureCode', ''),
      );
      if (isPartialNature)
        get(victim, 'regulationAmount', {})[
          REGULATION_AMOUNTS.RATE
        ] = provisionalExecutionRate;
    },
  );
  !isEmpty(rcMaterial) &&
    assignMaterialAmounts(risks, rcMaterial, judgementOnMeritsByVehicles);
  setRiskRecap(risks);
};

export const assignMaterialAmounts = (
  risks: any,
  rcMaterial: any,
  judgementOnMeritsByVehicles: any,
) => {
  get(risks[risks.indexOf(rcMaterial)], 'data', []).forEach((vehicle: any) => {
    const { judgementDamageAmount, appealDamageAmount } = getDamageAmount(
      judgementOnMeritsByVehicles,
      rcMaterial,
      vehicle,
    );
    get(vehicle, 'regulationAmount', {})[
      REGULATION_AMOUNTS.DAMAGE_AMOUNT
    ] = judgementDamageAmount;
    get(vehicle, 'regulationAmount', {})[
      REGULATION_AMOUNTS.APPEAL_DAMAGE_AMOUNT
    ] = appealDamageAmount;
  });
};

//to-delete-after-release
export const filterMinorVictims = (victims: any, lossDate: string) => {
  return victims.filter(
    (el: { dateOfBirth: string; injuryPostKey: string }) =>
      Number(
        moment(lossDate)
          .diff(el.dateOfBirth, 'years', true)
          .toFixed(2),
      ) > 18 && el.injuryPostKey != INJURY_POST_DECEASED,
  );
};
export const getNatureCodeTitle = (
  listings: any,
  natureCode: string,
  isAppeal: boolean,
) => {
  const encryptDocumentNature = get(listings, 'EncryptDocumentNature', []).find(
    (item: SagisConfig) => item?.ItemKey === natureCode,
  );
  const appealEncryptedNature = get(listings, 'EncryptDocumentNature', []).find(
    (item: SagisConfig) => item?.ItemKey === RELIQUAT_APPEL,
  );
  return get(encryptDocumentNature, 'ItemDescription', '') ===
    RELIQUAT_JUGEMENT_TITLE && isAppeal
    ? get(appealEncryptedNature, 'ItemDescription', '')
    : get(encryptDocumentNature, 'ItemDescription', '');
};
export const getSharedResponsibility = (judgementOnMerits: any) => {
  let sharedResponsibility = null;
  get(judgementOnMerits, 'jom.civjs', []).forEach((item: any) => {
    if (!isNil(item?.responsibilityShare))
      return (sharedResponsibility = item?.responsibilityShare);
  });
  isNil(sharedResponsibility) &&
    get(judgementOnMerits, 'jom.jomBeneficiaries', []).forEach((item: any) => {
      if (!isNil(get(item, 'beneficiary.victim.responsibilityShare')))
        return (sharedResponsibility = get(
          item,
          'beneficiary.victim.responsibilityShare',
        ));
    });
  return sharedResponsibility;
};
export const getTotalAmountWithoutRC = (
  judgementAmount: string,
  responsibilityShare: number,
) => {
  return (
    (parseFloat(removeCommaSeperator(judgementAmount)) / responsibilityShare) *
    100
  );
};
export const getCurrentSheet = (
  regulations: any,
  paymentId: string,
  sheetType: string,
) => {
  const currentRegulation =
    regulations.find((item: any) => {
      return get(item, 'sagisRegulationId') === paymentId;
    }) || {};
  const sheet =
    get(currentRegulation, 'documents', []).find(
      (item: any) => get(item, 'type', '') === sheetType,
    ) || {};
  return get(sheet, 'url');
};
export const getRecipientWhenFraud = (risk: any) => {
  switch (risk.riskName) {
    case RISK_NAME.CORPOREAL:
      const isFraudVictim = get(risk, 'data', []).some(
        (item: any) => !isEmpty(get(item, 'fraudVictim', {})),
      );
      return isFraudVictim && featureService.hasFeature(FEATURES.FRAUD_INFO);
    case RISK_NAME.MATERIEL:
      const isFraudVehicle = get(risk, 'data', []).some(
        (item: any) => !isEmpty(get(item, 'fraudVehicle', {})),
      );
      return isFraudVehicle && featureService.hasFeature(FEATURES.FRAUD_INFO);
  }
};
export const getRegulationRecipient = (risk: any, regulationRecipient: any) => {
  return getRecipientWhenFraud(risk) &&
    featureService.hasFeature(FEATURES.FRAUD_INFO)
    ? regulationRecipient.filter(
        (item: Config) => item.title === RECIPIENT.BNEJ,
      )
    : regulationRecipient;
};

export const resolveNewProvision = (sagisClaim: any, riskKey: string) => {
  const { riskProvision } = getRiskCorpoProvision(sagisClaim, riskKey);
  return riskProvision;
};

export const resolveJudgementDateSummarySheet = (rcCorpo: RcCorporeal) => {
  const isAppeal = get(rcCorpo.regulationInfo, REGULATIONS.IS_APPEAL);
  const appealDate = get(
    rcCorpo.regulationInfo,
    REGULATIONS.JUDGEMENT_DATE,
    '',
  );
  const judgementDate =
    get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE) !== NONE &&
    !isAppeal
      ? get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE, '')
      : get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE_JAF, '');

  return { appealDate, judgementDate };
};
export const uploadSheets = (
  base64Url: any,
  uploadDocumentRegulation: Function,
  createdRegulations: CorpoRegulation[],
  successCallBackSummarySheet: Function,
  successCallBackRecipientSheet: Function,
) => {
  const summarySheet = createdRegulations.map((item: any) => {
    return { regulationId: item.id, type: SHEETS.SUMMARY_SHEET };
  });
  const cnraSheet = createdRegulations
    .filter(
      (item: any) =>
        !isEmpty(item?.recipient) && item?.recipient.includes(RECIPIENT.CNRA),
    )
    .map((item: any) => {
      return { regulationId: item.id, type: SHEETS.CNRA_SHEET };
    });
  const cdraSheet = createdRegulations
    .filter(
      (item: any) =>
        !isEmpty(item?.recipient) && item?.recipient.includes(RECIPIENT.CDRA),
    )
    .map((item: any) => {
      return { regulationId: item.id, type: SHEETS.CDRA_SHEET };
    });

  !isEmpty(cdraSheet) &&
    uploadDocumentRegulation &&
    uploadDocumentRegulation({
      sheet: cdraSheet,
      base64Url: get(base64Url, 'cdraSheet'),
      successCallBack: successCallBackRecipientSheet(true),
    });
  !isEmpty(cnraSheet) &&
    uploadDocumentRegulation &&
    uploadDocumentRegulation({
      sheet: cnraSheet,
      base64Url: get(base64Url, 'cnraSheet'),
      successCallBack: successCallBackRecipientSheet(false),
    });
  !isEmpty(summarySheet) &&
    uploadDocumentRegulation &&
    uploadDocumentRegulation({
      sheet: summarySheet,
      base64Url: get(base64Url, 'summarySheet'),
      successCallBack: successCallBackSummarySheet(false),
    });
};
export const formatProvisionBody = (
  claimNumber: string,
  provisionInfo: any,
  sagisClaim: any,
  rcCorpo: RcCorporeal,
  claimId: number,
) => {
  const { contributors, victims } = provisionInfo;
  const riskID = parseInt(getRiskKey(sagisClaim, rcCorpo));
  const contributorProvisions = Object.values(contributors).map(contractor => ({
    riskID: get(contractor, 'riskID', riskID),
    reasonCode: '2',
    date: moment().toISOString(true),
    typeProvision: 5,
    amount: removeCommaSeperator(get(contractor, PROVISION.CONTRIBUTOR_AMOUNT)),
    intervenantCode: get(contractor, PROVISION.CONTRIBUTOR_CODE),
    intervenantTypeCode: get(contractor, PROVISION.CONTRIBUTOR_TYPE_CODE),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isManualEvaluation: false,
    createdBy: currentUserStorage?.get()?.username,
    claimId: claimId,
  }));
  const victimProvision = {
    riskID,
    reasonCode: '7',
    date: moment().toISOString(true),
    typeProvision: 1,
    amount: removeCommaSeperator(get(victims, PROVISION.VICTIM_AMOUNT)),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isManualEvaluation: true,
    createdBy: currentUserStorage?.get()?.username,
    claimId: claimId,
  };
  return {
    claimNumber,
    victimProvision: !isEmpty(get(victimProvision, 'amount'))
      ? victimProvision
      : undefined,
    contributorProvisions,
  };
};

export const isNatureRegulationPartial = (
  natureCode: any,
  regulationNatures: SagisConfig[],
) => {
  const partialNature =
    regulationNatures.find(item => item.ItemKey === natureCode) || {};
  return (
    get(partialNature, 'ItemDescription') === DOCUMENT_NATURE.PARTIAL.title
  );
};
export const getResponsibilityShare = (rcCorpo: any) => {
  const victim =
    get(rcCorpo, 'data', []).find(
      (item: any) => !!get(item, 'regulationAmount.responsibilityShare'),
    ) || {};
  return get(victim, 'regulationAmount.responsibilityShare', 0);
};
export const normalizeAndRemoveNumbersSpecialChars = (str: any) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
};
export const isCDRATownDifferent = (
  towns: SagisConfig[],
  risk: RcCorporeal,
) => {
  return (
    !isEmpty(get(risk.regulationInfo, REGULATIONS.REAL_BENEFICIARY, '')) &&
    !isEmpty(get(risk.regulationInfo, REGULATIONS.OPPOSING_LAWYER_CITY, '')) &&
    get(risk.regulationInfo, REGULATIONS.BENEFICIARY_RECIPIENT, '') ===
      RECIPIENT.CDRA &&
    normalizeAndRemoveNumbersSpecialChars(
      get(risk.regulationInfo, REGULATIONS.REAL_BENEFICIARY, ''),
    ) !==
      normalizeAndRemoveNumbersSpecialChars(
        get(
          getChosenCity(towns, risk, REGULATIONS.OPPOSING_LAWYER_CITY),
          'ItemDescription',
          '',
        ),
      )
  );
};
export const areVictimsRegulated = (
  rcCorpo: any,
  sagisClaim: any,
  paymentStatus: SagisConfig[],
) => {
  const result: boolean[] = [];
  const latestVictimDetails = getLatestRegulations(
    filterCanceledRegulations(
      paymentStatus,
      get(sagisClaim, 'regulations', []) || [],
    ),
  );

  get(rcCorpo, 'data', []).forEach((victim: VictimType) => {
    if (isEmpty(get(victim, 'beneficiaries', []))) {
      result.push(
        !isEmpty(
          latestVictimDetails.filter(
            (e: VictimType) =>
              parseInt(e?.victimKey) === parseInt(victim?.victimKey),
          ),
        ),
      );
    } else {
      get(victim, 'beneficiaries', []).forEach((nextOfKin: any) => {
        result.push(
          !isEmpty(
            latestVictimDetails.filter((e: any) => {
              return e?.nextOfKinKey === 0
                ? parseInt(e?.victimKey) === parseInt(victim?.victimKey)
                : parseInt(e?.nextOfKinKey) ===
                    parseInt(nextOfKin?.nextOfKinKey);
            }),
          ),
        );
      });
    }
  });

  const hasRegulation = result.every(Boolean);
  const hasBoth = result.includes(true) && result.includes(false);

  return { hasBoth, hasRegulation };
};
export const filterCanceledRegulations = (
  paymentStatus: SagisConfig[],
  regulations: any[] = [],
) => {
  const canceledStatusKey = paymentStatus
    ?.filter(
      status =>
        status.ItemDescription === PAYMENT_STATUS.CANCELED ||
        status.ItemDescription === PAYMENT_STATUS.REJECTED,
    )
    .map((el: any) => get(el, 'ItemKey'));
  return regulations.filter(
    regulation => !canceledStatusKey.includes(regulation?.paymentStatusCode),
  );
};

const getManagerInitials = (usernames: string[]): string[] => {
  return usernames.map(username => {
    const usernameParts = username.split('_');
    const MAX_PART_LENGTH =
      CHECK_PRINT_MAX_USERNAME_LENGTH / usernameParts.length;
    const formattedUsernameParts = usernameParts.map(part =>
      part.substring(0, MAX_PART_LENGTH),
    );
    return formattedUsernameParts
      .join('_')
      .substring(0, CHECK_PRINT_MAX_USERNAME_LENGTH);
  });
};

export const getAllDecidedBy = (
  timeLineHistory: any[],
  currentDecider: string,
): string[] => {
  if (isEmpty(timeLineHistory)) return currentDecider ? [currentDecider] : [];

  // compact remove empty strings
  const validators = compact(map(timeLineHistory, 'decidedBy'));

  if (currentDecider) {
    validators.push(currentDecider);
  }

  return getManagerInitials(validators);
};

export const formatProcedureNatureIds = (
  procedureNature: [],
  judgementNatureProcedureIds: [],
) => {
  const filtredProcedure = procedureNature.filter((el: { id: number }) =>
    judgementNatureProcedureIds?.includes(get(el, 'id') as never),
  );
  if (!isEmpty(filtredProcedure)) {
    return filtredProcedure;
  } else {
    return procedureNature;
  }
};

export const getElementTitle = (list: any, id: any) => {
  return get(
    list.find((el: any) => el.id === id),
    'title',
  );
};

export const getVictimNamesByCompareVictimKeys = (
  sagisVictims: any,
  corpoVictims: VictimType[],
) => {
  const sagisVictimsKeys = sagisVictims.map((item: any) => item.VictimKey);
  return corpoVictims
    .filter((el: VictimType) => !sagisVictimsKeys.includes(el.victimKey))
    .map((it: VictimType) => it.firstName + ' ' + it.lastName);
};
